import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { BrandService } from '../../../services/brand-config.service';
import { loaderComponent } from '../../shared/components/loader/loader.component';
import {
  DoctorReportService,
  MedicationEntry,
  ReportMedicationEvent,
  ReportSignalEntry,
  ReportSymptomEntry,
  ReportSideEffectEvent,
  ReportTimelinePoint,
  ReportViewData,
} from '../../services/doctor-report.service';
import { MaccabiService } from '../../services/maccabi.service';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { REPORT_FIXTURES } from './report-fixtures';
import { CLUSTER_VIEW_DEFS } from './symptom-clusters';
import {
  SenseMetric,
  metricValue,
  computeChange,
} from '../../results/panels/sense/sense-model';

/** A few points drawn on their own scale, with the two end dates under them. */
interface MiniChart {
  empty: boolean;
  segments: string[];
  dots: Array<{ x: number; y: number; title: string }>;
  firstLabel: string;
  lastLabel: string;
}

/** One measure followed across the mappings, as the trends grid reads it. */
interface DomainTrendRow {
  key: string;
  label: string;
  meta: string;
  /** The two ends of the series, kept apart so each number can be read left to
   *  right while the word between them follows the page. */
  firstText: string;
  lastText: string;
  valuesText: string;
  chart: MiniChart;
  /** One entry per mapping, for the rows that open as chips rather than a chart. */
  points?: Array<{ dateText: string; valueText: string }>;
}

interface SenseReportRow {
  metric: SenseMetric;
  labelKey: string;
  changeText: string;
  currentValue: string;
  prevValue: string;
  note: string;
}
interface SenseBlockVM {
  firstScan: boolean;
  rows: SenseReportRow[];
  sinceDate: string;
  summary: string;
  alsoReported: string;
}

interface TimelineSeries {
  key: string;
  label: string;
  color: string;
  dashed?: boolean;
  values: Array<{ scanNumber: number; week: number; date: string; value: number; x: number; y: number }>;
  points: string;
  changeText: string;
}

interface TimelineTick {
  scanNumber: number;
  x: number;
}

interface ClusterLine {
  label: string;
  score: number;
  // Worded per-symptom change ("עלה ב-2" / "ירד ב-3" / "ללא שינוי") + a css class.
  change: string;
  changeClass: 'u' | 'd' | 's';
}

interface ClusterSummary {
  key: string;
  label: string;
  // Hebrew name is primary; the abbreviation (VMS / פיזי) is secondary, since a
  // patient also reads this report and won't know the jargon (fix 43).
  abbrev: string;
  color: string;
  count: number;
  // Verdict WORD (השתפר / החמיר / מעורב / ללא שינוי) + its magnitude + the up/down
  // split, replacing the meaningless bare arrow. A mixed cluster reads "מעורב"
  // instead of a single net direction that hides the offsetting moves (fix 43).
  verdictWord: string;
  verdictClass: 'up' | 'down' | 'mix' | 'flat';
  // Fix 35 (revised): percent when the previous scan's cluster sum was > 0;
  // ABSOLUTE points ("עלה ב-3 נקודות") when it was 0 - a % there is a divide by
  // zero, not a bias, so a cluster that rose from nothing states the point change.
  magnitude: string;
  split: string;
  lines: ClusterLine[];
}

// PopulationRow interface REMOVED with the population comparison card
// (06_DOWNSTREAM_impact.md section 3).

// Fix 49: the cross-reference card shows these TWO FIXED pairs, in this order,
// every report. Each pairs a set of self-report symptoms with the digital cognitive
// domains that speak to the same construct. Fix 59 removed the standalone card; the
// pairs now drive the two-half interpretation-context line (fix 61) - half A the
// sleep/fatigue self-report, half B the concentration/memory self-report set beside
// the measured domains. It only places data side by side, never judges agreement.
const XREF_PAIRS: Array<{ key: string; selfKeys: string[]; digitalKeys: string[] }> = [
  {
    key: 'cognitive',
    selfKeys: ['brain_fog', 'forgetfulness', 'slow_recall', 'difficulty_concentrating'],
    digitalKeys: ['wordRetrieval', 'workingMemorySpan', 'attention'],
  },
  {
    key: 'sleep',
    selfKeys: ['daytime_sleepiness', 'early_awakening', 'unrefreshing_sleep', 'insomnia_staying_asleep'],
    digitalKeys: ['reactionTime', 'attention'],
  },
];

interface CognitiveRow {
  key: string;
  label: string;
  // Domain subtitle + one-line description, from the SAME results.th.tasks source
  // the patient's THINK screen renders (fix 24)  - the doctor gets the full context
  // the patient did, not just a name and a number.
  subtitle: string;
  what: string;
  score: number;
  displayValue: string;
  populationAvg: number;
  trend: string;
  // Within-person band label the PATIENT saw on her own THINK screen (strong /
  // mid / worth-tracking)  - shown beside each score so the doctor reads the same
  // frame the patient did, not a population claim.
  bandLabel: string;
  // Stage-B bonus domain not administered for this scan → render "not measured".
  notMeasured: boolean;
}

interface DomainScoreRow {
  label: string;
  value: number;
  color: string;
}

interface PatientListState {
  fromMaccabi?: boolean;
}

const TIMELINE_KEYS = [
  'hot_flashes',
  'night_sweats',
  'insomnia_staying_asleep',
  'low_energy',
  'motivation_drop',
  'mood_swings',
  'joint_pain',
  'brain_fog',
];

/**
 * The drawn palette, on the record's paper ground. The first three are the
 * evaluation design's own line colours; the rest extend them at the same muted
 * chroma and lightness so twelve lines still read as one chart rather than as a
 * box of highlighters. Deliberately clear of the semantic pair (good green,
 * concerning red), which the report spends only on a change against her baseline.
 */
const SERIES_PALETTE = [
  // the reference's own eight, in its order
  '#3268c4',
  '#009181',
  '#c77923',
  '#9051b5',
  '#c54f78',
  '#4c687b',
  '#718c2c',
  '#6446b2',
  // extended to twenty-four at the same chroma, so "add all" stays readable
  '#1f7a9e',
  '#a8562f',
  '#5e8f3e',
  '#8e4a86',
  '#b0563f',
  '#2e7d6b',
  '#7a5ba8',
  '#a8781f',
  '#3d5e8c',
  '#9e4560',
  '#4f7f4a',
  '#8a6f3a',
  '#2b6fa8',
  '#a35ba0',
  '#6b7a3c',
  '#565f8c',
];

/** The eight named lines keep a stable colour across mappings; see symptomColor
 *  for everything else. */
const SYMPTOM_COLORS: Record<string, string> = {
  hot_flashes: SERIES_PALETTE[0],
  night_sweats: SERIES_PALETTE[1],
  insomnia_staying_asleep: SERIES_PALETTE[2],
  low_energy: SERIES_PALETTE[3],
  motivation_drop: SERIES_PALETTE[4],
  mood_swings: SERIES_PALETTE[5],
  joint_pain: SERIES_PALETTE[6],
  brain_fog: SERIES_PALETTE[7],
};

// Fix 48/56 (round 7): the cluster card is derived ENTIRELY from the catalog cluster
// map (symptom-clusters.ts, from 09_symptom_cluster_map.json) - 100 symptoms -> 20
// clusters. No hardcoded cluster/symptom list lives here anymore; a new catalog
// symptom appears in the card with no report code change.

// POPULATION_AVERAGES / POP_DOT_COLOR / INVERTED_SEVERITY REMOVED with the
// population comparison card (06_DOWNSTREAM_impact.md section 3): the report no
// longer places the patient against any cohort.

const COGNITIVE_AVERAGES: Record<string, number> = {
  digitRecall: 78,
  shortTermRecall: 76,
  wordRetrieval: 71,
  reactionTime: 63,
  workingMemorySpan: 74,
  delayedRecall: 72,
};

// Symptom domains  - mirrors the consumer symptoms panel (symptoms-vm.ts DOMAINS)
// so the doctor report shows the SAME domain scores the patient saw (e.g. Sleep
// 5.8): average of the reported (>0) symptoms in each domain, one decimal.
const SYMPTOM_DOMAINS: Array<{ key: string; labelKey: string; ids: string[] }> = [
  { key: 'sleep', labelKey: 'results.sym.domains.sleep', ids: ['insomnia_falling_asleep', 'insomnia_staying_asleep', 'early_awakening', 'night_sweats', 'unrefreshing_sleep', 'daytime_sleepiness'] },
  { key: 'energy', labelKey: 'results.sym.domains.energy', ids: ['energy_dips', 'motivation_drop', 'weight_gain'] },
  { key: 'cognition', labelKey: 'results.sym.domains.cognition', ids: ['brain_fog', 'forgetfulness', 'difficulty_concentrating', 'slow_recall'] },
  { key: 'mood', labelKey: 'results.sym.domains.mood', ids: ['mood_swings', 'anxiety', 'low_mood', 'irritability', 'stress_sensitivity'] },
];

// Fix 19: ONE severity band system, identical to the consumer symptom report
// (mild 0-4, moderate 5-6, pronounced 7-10). Every severity tag in this report  -
// the appendix tiers AND the "needs attention" chips  - derives from here, so the
// provider and the patient never disagree on a symptom's tier (no third system).
const SEVERITY_BANDS: Array<{ max: number; key: 'low' | 'mid' | 'high' }> = [
  { max: 4, key: 'low' },
  { max: 6, key: 'mid' },
  { max: 10, key: 'high' },
];

// Fix 24: cognitive signal key -> results.th.tasks domain key (the SAME source the
// patient's THINK screen renders), so the six domain names / subtitles / one-line
// descriptions are identical in both reports. wordRetrieval was mislabeled "verbal
// memory"  - it is verbal FLUENCY, a different construct  - now sourced from the map.
// Legacy-only signal keys (shortTermRecall, digitRecall) fall back to v4.signal.
const COGNITIVE_DOMAIN_KEY: Record<string, string> = {
  reactionTime: 'speed',
  wordRetrieval: 'words',
  workingMemorySpan: 'memory',
  delayedRecall: 'recall',
  flexibility: 'flexibility',
  attention: 'attention',
};

// Fix 20 (THINK continuity): the 4 composite signal keys  - mirrors the consumer
// ScoreCalculationService.COMPOSITE_TASKS (Stage-B bonus domains excluded), so the
// THINK number shown here equals the one the patient saw.
/** The stored bucket keys map to the questionnaire own label keys. */
const LAST_PERIOD_LABEL: Record<string, string> = {
  within_month: 'withinMonth',
  m1_3: 'm1_3',
  m3_6: 'm3_6',
  m6_12: 'm6_12',
  over_year: 'overYear',
  not_sure: 'notSure',
};

/** The three panels of the record layout. */
export type ReportTab = 'summary' | 'timeline' | 'detail';

const THINK_COMPOSITE_KEYS = new Set([
  'reactionTime',
  'wordRetrieval',
  'workingMemorySpan',
  'delayedRecall',
]);

@Component({
  selector: 'app-report-view',
  standalone: true,
  imports: [CommonModule, MatIconModule, loaderComponent, TranslatePipe],
  templateUrl: './report-view.component.html',
  styleUrl: './report-view.component.scss',
})
export class ReportViewComponent implements OnInit, OnDestroy {
  scanId = '';
  loading = true;
  /** B7: the report load failed -> loader shows Retry instead of a blank page. */
  loadFailed = false;
  generatingPdf = false;
  copyState = '';
  /** Shown under the print button when the browser never opened a print dialog. */
  printHint = '';
  /** Guards the print button while the server renders the PDF. */
  printBusy = false;
  brandName = '';
  logoSrc = '';
  data: ReportViewData | null = null;
  isMaccabiRoute = false;
  // The QA render-only preview is the PROVIDER's report, so it reads in the
  // clinician's voice like the portal does. ?voice=self opens the same fixture
  // in the voice the woman gets.
  isProviderPreview = false;
  // A3: original browser tab title, restored on leave. The report sets document.title
  // to the clinician-share filename so the browser's Save-as-PDF defaults to it.
  private prevDocTitle = '';

  readonly chartWidth = 700;
  readonly chartHeight = 320;
  readonly chartLeft = 40;
  readonly chartTop = 10;
  readonly chartInnerWidth = 640;
  readonly chartInnerHeight = 260;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private doctorReportService: DoctorReportService,
    private maccabiService: MaccabiService,
    private brandService: BrandService,
    private translation: TranslationService
  ) {
    this.brandName = this.brandService.config.brandName;
    this.logoSrc = this.brandService.logoSrc;
  }

  /** Translate a doctorReport.reportView.v4.* key with optional interpolation params. */
  rv(key: string, params?: Record<string, unknown>): string {
    return this.voiced('v4', key, params);
  }

  /**
   * Danna 16.09: one report, two readers. The clinician reads ABOUT her ("WHY SHE
   * IS HERE, IN HER WORDS"); she reads her own copy, and there it has to talk TO
   * her ("WHY YOU ARE HERE, IN YOUR WORDS"). So any v4/v5 key that has a
   * `<key>Self` sibling in the locale file resolves to that sibling when she is
   * the reader, and to the untouched original for everyone in the portal. A key
   * with no Self sibling (labels, scales, counts) reads the same either way and
   * needs no second version.
   */
  private voiced(ns: 'v4' | 'v5', key: string, params?: Record<string, unknown>): string {
    return this.tv(`doctorReport.reportView.${ns}.${key}`, params);
  }

  /** Translate a full key path in the reader's voice (`<path>Self` when it is hers). */
  tv(path: string, params?: Record<string, unknown>): string {
    if (!this.isClinicianVoice) {
      const selfPath = `${path}Self`;
      const self = this.translation.translate(selfPath, params);
      if (self && self !== selfPath) return self;
    }
    return this.translation.translate(path, params);
  }

  /** True wherever the report is read by someone other than the woman herself. */
  get isClinicianVoice(): boolean {
    return this.isMaccabiRoute || this.isProviderPreview || this.printClinicianVoice;
  }

  /** Translate a full key path, falling back to `fallback` when the key is missing
   *  (i18next returns the path itself when a key is absent). */
  private tx(path: string, fallback: string, params?: Record<string, unknown>): string {
    const value = this.translation.translate(path, params);
    return value && value !== path ? value : fallback;
  }

  async ngOnInit(): Promise<void> {
    document.body.classList.add('printing-doctor-report');
    // Admin "open in the link's locale": ?rlocale=he|en|en-GB|en-IN forces the
    // report to render in the locale the woman actually saw, independent of the
    // admin's own saved language (doesn't persist it). No-op when the param is
    // absent (normal owner/preview views).
    const rlocale = this.route.snapshot.queryParamMap.get('rlocale');
    if (rlocale) {
      await this.translation.applyReportLocale(rlocale);
    }
    // The headless print route (/:lang/report-print/:scanId?t=): the token
    // stands in for the session, so the data comes from the public endpoint.
    this.printToken = this.route.snapshot.queryParamMap.get('t') || '';
    // The clinician portal prints the clinician's own copy of the same report.
    this.printClinicianVoice =
      !!this.printToken &&
      this.route.snapshot.queryParamMap.get('voice') === 'clinician';
    this.isMaccabiRoute = this.isMaccabiDoctorPortalRoute();
    this.isProviderPreview =
      /\/provider-report\/preview/.test(this.router.url.toLowerCase()) &&
      this.route.snapshot.queryParamMap.get('voice') !== 'self';
    // QA render-only preview: /provider-report/preview?fixture=worst_case renders
    // a fake payload with no DB / auth, for the edge cases the real report can't hit.
    const fixture = this.route.snapshot.queryParamMap.get('fixture');
    if (fixture && REPORT_FIXTURES[fixture]) {
      this.data = REPORT_FIXTURES[fixture];
      this.applyReportDocTitle();
      this.loading = false;
      return;
    }
    this.scanId =
      this.route.snapshot.params['scanId'] ||
      this.route.snapshot.params['demoId'] ||
      '';
    await this.loadData();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('printing-doctor-report');
    if (this.prevDocTitle && typeof document !== 'undefined') {
      document.title = this.prevDocTitle;
      this.prevDocTitle = '';
    }
  }

  /** A3: browsers use document.title as the default Save-as-PDF filename, so we set
   *  it to the clinician-share pattern the moment the report data is ready (covers
   *  both the toolbar Print button and the browser's own Ctrl+P). */
  private applyReportDocTitle(): void {
    if (typeof document === 'undefined' || !this.data) return;
    if (!this.prevDocTitle) this.prevDocTitle = document.title;
    document.title = this.reportFileName();
  }

  /** A3: MARKABLE_Clinician_Share_Report_First_Last_YYYY-MM-DD (name omitted when
   *  unknown -> MARKABLE_Clinician_Share_Report_YYYY-MM-DD). */
  private reportFileName(): string {
    const parts = ['MARKABLE_Clinician_Share_Report', this.fileNamePart(this.data?.patientName), this.fileDatePart(this.data?.checkDate)];
    return parts.filter(Boolean).join('_');
  }

  private fileNamePart(name?: string): string {
    const clean = (name || '').trim();
    if (!clean) return '';
    // Strip filesystem-illegal chars, collapse inner whitespace to single underscores.
    return clean
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private fileDatePart(checkDate?: string): string {
    const ts = this.parseReportDate(checkDate) ?? Date.now();
    const d = new Date(ts);
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  }

  /** Set on the guard-free print route: renders one report for the PDF. */
  printToken = '';
  /** That render is the clinician's copy, not hers (portal print). */
  printClinicianVoice = false;

  async loadData(): Promise<void> {
    this.loading = true;
    this.loadFailed = false;
    try {
      if (this.printToken) {
        this.data = await this.doctorReportService.getPrintViewData(
          this.printToken
        );
        if (this.data) this.applyReportDocTitle();
        else this.loadFailed = true;
        return;
      }
      if (this.isMaccabiRoute) {
        const response = await this.maccabiService.getReportViewData(this.scanId);
        this.data = response?.data || null;
      } else {
        this.data = await this.doctorReportService.getReportViewData(this.scanId);
      }
      if (!this.data) {
        if (this.isMaccabiRoute) {
          this.data = null;
        } else {
          this.router.navigate(['/app/doctor-report', this.scanId]);
        }
      } else {
        this.applyReportDocTitle();
      }
    } catch (error) {
      console.error('ReportViewComponent: failed to load data:', error);
      if (this.isMaccabiRoute) {
        // The clinician view has nowhere to bounce to: show the failure with a
        // Retry rather than an empty card.
        this.data = null;
        this.loadFailed = true;
      } else {
        this.router.navigate(['/app/doctor-report', this.scanId]);
      }
    } finally {
      this.loading = false;
    }
  }

  get medications(): MedicationEntry[] {
    return this.data?.medications || [];
  }

  get hrtMedications(): MedicationEntry[] {
    return this.medications.filter((m) => this.isHrt(m));
  }

  get otherMedications(): MedicationEntry[] {
    return this.medications.filter((m) => !this.isHrt(m));
  }

  get adherencePercent(): number {
    if (!this.medications.length) return 0;
    const scores = this.medications.map((m) => {
      switch (m.consistency) {
        case 'very_consistently':
          return 100;
        case 'mostly_consistently':
          return 85;
        case 'sometimes_missed':
          return 60;
        case 'often_missed':
          return 35;
        default:
          return 75;
      }
    });
    return Math.round(scores.reduce((sum, value) => sum + value, 0) / scores.length);
  }

  get symptomBurden(): number {
    // Fix 20: the SAME tier-weighted saturation "symptom load" the patient saw on
    // her results (symptoms-vm.ts)  - mild×1 + moderate×2 + pronounced×3, scaled to
    // a soft ceiling  - NOT a raw mean×10. So the number the doctor sees (e.g. 89)
    // is the number the patient will cite in the room.
    const map = this.currentSymptoms;
    if (!Object.keys(map).length) return 0;
    const vals = Object.values(map)
      .map((v) => Number(v))
      .filter((v) => v > 0);
    const pronounced = vals.filter((v) => v >= 7).length;
    const moderate = vals.filter((v) => v > 4 && v < 7).length;
    const mild = vals.filter((v) => v > 0 && v <= 4).length;
    const weighted = pronounced * 3 + moderate * 2 + mild;
    if (weighted <= 0) return 0;
    // K scales with the active catalog size (05_SCORING_100_items.md, section 4b):
    // K = 20·(nActive/60). nActive defaults to 60 until it is stored per-scan, so
    // today (60-item catalog) K=20 and the number is unchanged; a 100-item scan
    // yields K=33.33. MUST stay mirrored with symptoms-vm.ts + scan-cohort.
    const K = 20 * (this.scanNActive / 60);
    return Math.max(1, Math.round(97 * (1 - Math.exp(-weighted / K))));
  }

  /** Active catalog size for the current scan (number of symptom items shown).
   *  Feeds the burden K-ramp. Defaults to 60 until catalog-version-per-scan is
   *  stored (06_DOWNSTREAM_impact.md section 2). */
  get scanNActive(): number {
    const n = (this.data as any)?.nActive;
    return typeof n === 'number' && n > 0 ? n : 60;
  }

  /** THINK composite (fix 20): mean of the four measured composite-task domain
   *  scores  - identical to the consumer calculateCompositeScore. null when no
   *  composite domain was measured. */
  get thinkScore(): number | null {
    const rows = (this.cognitiveRows || []).filter(
      (r) => !r.notMeasured && THINK_COMPOSITE_KEYS.has(r.key),
    );
    if (!rows.length) return null;
    return Math.round(rows.reduce((sum, r) => sum + r.score, 0) / rows.length);
  }

  /** Fix 20: a single secondary line (no ring / label / colour / trend) echoing
   *  the two headline numbers the patient saw  - symptom load and THINK  - so the
   *  clinician recognizes them if she cites them. 20b (trends) stays frozen: no
   *  delta, no trajectory, just the current figures. */
  get continuityLine(): string {
    const burden = this.symptomBurden;
    const think = this.thinkScore;
    const hasBurden = burden > 0;
    const hasThink = think !== null;
    if (hasBurden && hasThink) return this.rv('continuityLine', { burden, think });
    if (hasBurden) return this.rv('continuityBurdenOnly', { burden });
    if (hasThink) return this.rv('continuityThinkOnly', { think });
    return '';
  }

  get hrtWeeks(): number | null {
    const starts = this.medications
      .map((m) => (m.startDate ? new Date(m.startDate).getTime() : 0))
      .filter(Boolean);
    if (!starts.length) return null;
    const first = Math.min(...starts);
    const reportTime = this.parseReportDate(this.data?.checkDate) || Date.now();
    return Math.max(0, Math.round((reportTime - first) / 604800000));
  }

  get timelinePoints(): ReportTimelinePoint[] {
    return this.data?.symptomTimeline || [];
  }

  /** Number of checks behind this report. Drives the single-check snapshot mode:
   *  with one data point there is no trend, so every "over time" element is
   *  suppressed until a second check exists (mirrors the consumer progress card). */
  get checkCount(): number {
    return this.data?.scanCount ?? this.timelinePoints.length;
  }

  get isSingleCheck(): boolean {
    return this.checkCount <= 1;
  }

  /** True once there are >= 2 checks: trends, deltas and the timeline turn on. */
  get hasTrends(): boolean {
    return this.checkCount >= 2;
  }

  /** Neutral, non-judgmental adherence color: green only when genuinely good,
   *  amber sub-threshold, red for poor  - never signal "good" for poor adherence. */
  get adherenceColor(): string {
    const percent = this.adherencePercent;
    if (percent >= 80) return '#1e6b34';
    if (percent >= 60) return '#8b6433';
    return '#b3261e';
  }

  get adherenceIsGood(): boolean {
    return this.medications.length > 0 && this.adherencePercent >= 80;
  }

  get adherenceConcern(): boolean {
    return this.medications.length > 0 && this.adherencePercent < 80;
  }

  get hasCognitiveConcern(): boolean {
    return this.cognitiveRows.some((row) => row.score < row.populationAvg);
  }

  get timelineCoverageText(): string {
    const scanCount = this.data?.scanCount ?? this.timelinePoints.length;
    const symptomCount =
      this.data?.symptomScanCount ??
      this.timelinePoints.filter((point) => Object.keys(point.symptoms || {}).length > 0).length;
    if (!scanCount) return this.rv('timelineNoScans');
    return this.rv('timelineCoverage', { scans: scanCount, symptomScans: symptomCount });
  }

  get timelineMaxWeek(): number {
    const values = [
      this.hrtWeeks || 0,
      ...this.timelinePoints.map((point) => point.week),
      ...(this.data?.hrtEvents || []).map((event) => event.week),
    ];
    return Math.max(1, ...values);
  }

  get timelineMaxScanNumber(): number {
    const maxFromPoints = this.timelinePoints.reduce(
      (max, point, index) => Math.max(max, point.scanNumber || index + 1),
      0
    );
    return Math.max(1, this.data?.scanCount || 0, maxFromPoints);
  }

  get scanAxisMarks(): TimelineTick[] {
    return this.timelinePoints.map((point, index) => {
      const scanNumber = point.scanNumber || index + 1;
      return { scanNumber, x: this.xForScanNumber(scanNumber) };
    });
  }

  get timelineTicks(): TimelineTick[] {
    const max = this.timelineMaxScanNumber;
    const step = max <= 30 ? 1 : max <= 80 ? 5 : 10;
    const raw = [1];
    for (let value = step; value < max; value += step) raw.push(value);
    raw.push(max);
    const scanNumbers = Array.from(new Set(raw.filter((value) => value >= 1 && value <= max)));
    return scanNumbers.map((scanNumber) => ({
      scanNumber,
      x: this.xForScanNumber(scanNumber),
    }));
  }

  get hrtEvents(): ReportMedicationEvent[] {
    return this.data?.hrtEvents || [];
  }

  get timelineSeries(): TimelineSeries[] {
    const points = this.timelinePoints;
    if (!points.length) return [];

    // Which lines are drawn is hers: the picker in the timeline panel writes
    // the selection, and until she touches it the default set is used.
    if (!this.showSymptomLayer) return [];
    const keys = this.activeSeriesKeys.filter((key) =>
      points.some((point) => point.symptoms?.[key] !== undefined)
    );

    return keys.map((key) => {
      const values = points
        .filter((point) => point.symptoms?.[key] !== undefined)
        .map((point, index) => {
          const value = Math.max(0, Math.min(10, Number(point.symptoms[key] || 0)));
          const scanNumber = point.scanNumber || index + 1;
          return {
            scanNumber,
            week: point.week,
            date: point.date,
            value,
            // Fix 29a: plot on a real DATE axis, not the 1..61 scan-number index that
            // crammed every point into the right 4% and overlapped the tick labels.
            x: this.xForDate(point.date),
            y: this.yForScore(value),
          };
        });
      const current = values[values.length - 1]?.value ?? 0;
      // Fix 25: legend delta uses the SAME previous-rated baseline as the summary /
      // attention card (not the first point of the window), so a symptom shows one
      // delta everywhere.
      const previous = this.previousRatedValue(key);
      return {
        key,
        label: this.symptom(key),
        color: this.symptomColor(key),
        dashed: key === 'motivation_drop' || key === 'brain_fog',
        values,
        points: values.map((value) => `${value.x},${value.y}`).join(' '),
        changeText: previous === null ? '' : this.changeText(previous, current),
      };
    });
  }

  /** Fix 43 (+ revised 35): a redesigned cluster card. Each shows the symptoms
   *  present this scan (name / score / worded change), a verdict WORD with its
   *  magnitude (percent, or absolute points when the previous sum was 0) and an
   *  up/down split. No stats jargon, no per-cluster sparkline (the unified chart
   *  below already carries the trajectory). */
  get clusterSummaries(): ClusterSummary[] {
    const points = this.timelinePoints;
    if (!points.length) return [];
    const cur = this.currentSymptoms;

    return CLUSTER_VIEW_DEFS.map((def) => {
      // Fix 62 (round 8): show a symptom row only when the current value OR the
      // previous rated value is > 0. The VALUE decides, not the change - a stable
      // 10/10 is the finding the doctor needs, a 0-that-stayed-0 is noise. This is
      // the same rule fix 47 applied to the first-scan card, now on the follow-up
      // (compare) render path too. A cluster whose every row hides is dropped.
      const present = def.symptomKeys.filter((k) => {
        const curr = Number(cur[k] || 0);
        const prv = this.previousRatedValue(k);
        return curr > 0 || (prv !== null && prv > 0);
      });
      if (!present.length) return null;

      let ups = 0, downs = 0;
      const rawLines = present.map((key, index) => {
        const score = Number(cur[key] || 0);
        const prev = this.previousRatedValue(key);
        const delta = prev === null ? 0 : score - prev;
        let changeClass: 'u' | 'd' | 's' = 's';
        let change = this.rv('clNoChange');
        if (delta > 0) { ups++; changeClass = 'u'; change = this.rv('lineUp', { n: delta }); }
        else if (delta < 0) { downs++; changeClass = 'd'; change = this.rv('lineDown', { n: -delta }); }
        // A present symptom with no delta (stable, still > 0) keeps its "ללא שינוי"
        // row - a real finding - but doesn't tip the verdict up or down (fix 43).
        return { label: this.symptom(key), score, delta, change, changeClass, index };
      });
      // Order: score desc, then bigger drop first, then def order (stable).
      const lines: ClusterLine[] = rawLines
        .sort((a, b) => b.score - a.score || a.delta - b.delta || a.index - b.index)
        .map(({ label, score, change, changeClass }) => ({ label, score, change, changeClass }));

      let verdictWord: string;
      let verdictClass: 'up' | 'down' | 'mix' | 'flat';
      if (ups > 0 && downs > 0) { verdictWord = this.rv('clMixed'); verdictClass = 'mix'; }
      else if (ups > 0) { verdictWord = this.rv('clWorsened'); verdictClass = 'up'; }
      else if (downs > 0) { verdictWord = this.rv('clImproved'); verdictClass = 'down'; }
      else { verdictWord = this.rv('clNoChange'); verdictClass = 'flat'; }

      // Fix 63 (round 8): drop the percent. It was computed on a subset (2 of 8
      // "moved" while 6 never did) yet shown as the whole-cluster verdict, so a
      // quarter-cluster improvement read "down 100%". Use a COUNT of the shown
      // items (option A): "{n} of {shown} shown are lower". Mixed states its own
      // split. A fully-stable cluster shows only the "ללא שינוי" verdict word.
      const shown = present.length;
      let magnitude = '';
      if (ups > 0 && downs > 0) {
        magnitude = [
          this.rv(downs === 1 ? 'clMixLowerOne' : 'clMixLower', { n: downs }),
          this.rv(ups === 1 ? 'clMixHigherOne' : 'clMixHigher', { n: ups }),
        ].join(', ');
      } else if (downs > 0) {
        magnitude = this.rv('clShownLower', { n: downs, shown });
      } else if (ups > 0) {
        magnitude = this.rv('clShownHigher', { n: ups, shown });
      }

      return {
        key: def.key,
        label: this.cluster(def.key),
        abbrev: this.tx(`doctorReport.reportView.v4.clusterAbbrev.${def.key}`, ''),
        color: def.color,
        count: present.length,
        verdictWord,
        verdictClass,
        magnitude,
        split: '',
        lines,
      };
    })
      .filter((summary): summary is ClusterSummary => summary !== null)
      // Fix 56 (round 7): with 20 map-derived clusters, order matters - most-severe
      // cluster (by its top symptom score) first, so the reader scans top-down.
      .sort((a, b) => (b.lines[0]?.score || 0) - (a.lines[0]?.score || 0));
  }

  /** Fix 45: first-scan "opening picture" - the same clusters, but composition only
   *  (name + score), NO verdict / magnitude / direction / sparkline, since there is
   *  no previous scan to compare against. */
  get compositionClusters(): Array<{ label: string; abbrev: string; color: string; count: number; lines: Array<{ label: string; score: number }> }> {
    const cur = this.currentSymptoms;
    return CLUSTER_VIEW_DEFS.map((def) => {
      // Fix 47: only symptoms rated ABOVE 0. A 0/10 is a valid answer ("I don't have
      // this"), but zero-rows bury the symptoms she does have. Fix 55 (round 7): a
      // fully-zero cluster is simply not shown - no footer declaring it zero, which
      // was a false negative claim before the full cluster map (fix 56) landed.
      const present = def.symptomKeys.filter((k) => Number(cur[k] || 0) > 0);
      if (!present.length) return null;
      const lines = present
        .map((k) => ({ label: this.symptom(k), score: Number(cur[k] || 0) }))
        .sort((a, b) => b.score - a.score);
      return {
        label: this.cluster(def.key),
        abbrev: this.tx(`doctorReport.reportView.v4.clusterAbbrev.${def.key}`, ''),
        color: def.color,
        count: present.length,
        lines,
      };
    })
      .filter((c): c is { label: string; abbrev: string; color: string; count: number; lines: Array<{ label: string; score: number }> } => c !== null)
      // Fix 47: clusters sorted by their highest score, most severe first.
      .sort((a, b) => (b.lines[0]?.score || 0) - (a.lines[0]?.score || 0));
  }

  /** Fix 55b (round 7): the count of symptoms rated >0 this visit  - the TRUE count,
   *  independent of the cluster map. Stated in the cluster-card title as a permanent
   *  self-check: if the title says 7 and only 2 rows render (a symptom missing from
   *  the map), the mismatch screams off the page instead of reading as a clean card
   *  that silently drops five findings. */
  get compositionRatedCount(): number {
    return Object.values(this.currentSymptoms).filter((v) => Number(v) > 0).length;
  }

  get compositionSubText(): string {
    const count = this.compositionRatedCount;
    return count === 1 ? this.rv('compositionSubCountOne') : this.rv('compositionSubCount', { count });
  }

  // Population comparison getters REMOVED (06_DOWNSTREAM_impact.md section 3):
  // populationRows / populationNoCohortCount / populationNoCohortLine are gone with
  // the card. No population reference remains in the report.

  get cognitiveRows(): CognitiveRow[] {
    return (this.data?.cognitiveSignals || []).map((signal) => {
      if (signal.notMeasured) {
        return {
          key: signal.key,
          label: this.cognitiveDomainLabel(signal.key, 'title'),
          subtitle: this.cognitiveDomainLabel(signal.key, 'subtitle'),
          what: this.cognitiveDomainLabel(signal.key, 'what'),
          score: 0,
          displayValue: '',
          populationAvg: COGNITIVE_AVERAGES[signal.key] ?? 70,
          trend: '',
          bandLabel: '',
          notMeasured: true,
        };
      }
      const score = this.cognitiveScore(signal);
      return {
        key: signal.key,
        label: this.cognitiveDomainLabel(signal.key, 'title'),
        subtitle: this.cognitiveDomainLabel(signal.key, 'subtitle'),
        what: this.cognitiveDomainLabel(signal.key, 'what'),
        score,
        displayValue: signal.value,
        populationAvg: COGNITIVE_AVERAGES[signal.key] ?? 70,
        // No trend from a single check  - a trend tag on one data point reads as
        // fabricated. Cross-sectional vs-average bar stays; the trend turns on at check 2.
        trend: this.hasTrends ? this.signalTrend(signal) : '',
        // Within-person band the patient saw (same 70/45 thresholds + labels as
        // the consumer THINK screen), so text/graph frame the score her way.
        bandLabel: this.withinPersonBand(score),
        notMeasured: false,
      };
    });
  }

  /** The within-review band label (strong / mid / worth-tracking), same 70/45
   *  thresholds the patient saw on THINK. Fix 39 (reverses R4-36): NO grammatical
   *  person  - the report is read by both the patient and the clinician, so any
   *  pronoun ("אצלך"/"אצלה") is wrong for one of them. ONE shared string
   *  (results.th.band*), rewritten to the pronoun-free "מהגבוהים בסקירה". */
  private withinPersonBand(score: number): string {
    const key = score >= 70 ? 'results.th.bandStrong' : score >= 45 ? 'results.th.bandMid' : 'results.th.bandLow';
    return this.translation.translate(key);
  }

  /** Symptom-domain scores (Sleep / Energy / Cognition / Mood), the SAME numbers
   *  the patient saw on her results (e.g. Sleep 5.8). Average of the reported (>0)
   *  symptoms per domain, one decimal; a domain with no reported symptoms drops. */
  get domainScores(): DomainScoreRow[] {
    const map = this.currentSymptoms;
    return SYMPTOM_DOMAINS.map((d) => {
      const vals = d.ids.map((id) => Number(map[id]) || 0).filter((v) => v > 0);
      if (!vals.length) return null;
      const value = +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
      return {
        label: this.tx(d.labelKey, d.key),
        value,
        color: '#87643e',
      };
    }).filter((row): row is DomainScoreRow => row !== null);
  }

  // step3 addendum (2026-08-08): the three doctor-report sentences that claim the
  // cognitive scores are above/below the comparison average are SILENCED until we
  // have 50+ SHARP-v2 scans (same rationale as hiding the patient-facing cohort
  // comparison  - we don't build a clinical claim on a number we won't show her).
  // The report still shows the cognitive scores; only the above/below-average
  // CLAIM is dropped (the digital-observation sentence, the amber concern note,
  // and the EHR attention line). The connector is forced neutral so it can't
  // dangle. Re-enable by flipping this flag back to false.
  readonly hideCognitiveAvgClaim = true;

  /** Whether to surface the average-derived cognitive-concern note anywhere
   *  (amber ⚠ note + EHR attention line). Off while hideCognitiveAvgClaim. */
  get showCognitiveConcernNote(): boolean {
    return !this.hideCognitiveAvgClaim && this.hasCognitiveConcern;
  }

  /** Fix 22 / 34: symptoms that dropped to 0 since the previous rated scan  - a
   *  positive change a clinician reading only the appendix (which lists rated>0
   *  symptoms) would otherwise miss. Derived from ALL symptoms (union of current +
   *  previous rated), NOT the capped improvedResponseRows/timelineSeries top-8, so
   *  the three cognitive symptoms that resolved (difficulty_concentrating,
   *  forgetfulness, slow_recall) are no longer dropped (fix 34). */
  get resolvedSinceLast(): Array<{ label: string; prev: number }> {
    const prevPoint = this.previousRatedPoint;
    if (!prevPoint) return [];
    const prev = prevPoint.symptoms || {};
    // Fix 34: a symptom is "resolved" when it was PROMINENT last visit (>=7, the
    // same bar as prominentSymptoms) and is 0 now. Derived from every rated symptom
    // (not the capped improvedResponseRows), so the three cognitive symptoms that
    // resolved (difficulty_concentrating 9->0, forgetfulness 10->0, slow_recall
    // 10->0) are no longer dropped. The >=7 floor keeps it to genuine, notable
    // resolutions (each scan rates all ~60 symptoms, so many drift to 0 between
    // visits; only a prominent one clearing is the clinical "resolved") and makes
    // the count line up with what the cluster breakdown surfaces.
    return Object.entries(this.currentSymptoms)
      .map(([key, value]) => ({ key, cur: Number(value ?? 0), prv: Number(prev[key] ?? 0) }))
      .filter((r) => r.cur === 0 && r.prv >= 7)
      .sort((a, b) => b.prv - a.prv)
      .map((r) => ({ label: this.symptom(r.key), prev: r.prv }));
  }

  /** Fix 40: every CLUSTER symptom whose rating dropped since the previous rated
   *  scan (not only to 0), most-improved first, with before + after. A row whose
   *  CURRENT value is still >=7 is tagged "עדיין בולט" so a clinician scanning the
   *  green card can't drop priority on a symptom that improved but is still severe.
   *  Symmetric with the "rose" card (both list every move in their direction). */
  get droppedSinceLast(): Array<{ label: string; prev: number; current: number; stillProminent: boolean }> {
    const out: Array<{ label: string; prev: number; current: number; drop: number; stillProminent: boolean }> = [];
    for (const def of CLUSTER_VIEW_DEFS) {
      for (const key of def.symptomKeys) {
        const raw = this.currentSymptoms[key];
        if (raw === undefined) continue;
        const prev = this.previousRatedValue(key);
        const current = Number(raw);
        if (prev !== null && current < prev) {
          out.push({ label: this.symptom(key), prev, current, drop: prev - current, stillProminent: current >= 7 });
        }
      }
    }
    // Stable sort by drop size desc → ties keep the cluster/def iteration order.
    return out.sort((a, b) => b.drop - a.drop);
  }

  /** Fix 32: every currently prominent symptom (>=7), sorted most severe first -
   *  the SEVERITY axis. A symptom on 10/10 that did not change is still the finding,
   *  so the medical-record summary must carry it even though the change axis
   *  (worsened list) would drop it. */
  get prominentSymptoms(): Array<{ label: string; value: number }> {
    const current = this.currentSymptoms;
    return Object.entries(current)
      .map(([key, value]) => ({ key, value: Number(value || 0) }))
      .filter((row) => row.value >= 7)
      .sort((a, b) => b.value - a.value)
      .map((row) => ({ label: this.symptom(row.key), value: row.value }));
  }

  get ehrSummary(): string {
    if (!this.data) return '';
    const meds = this.medications.map((m) => m.name).filter(Boolean).join(', ') || this.rv('ehrNotReported');
    // Fix 54: the reported unusual sensations, if any - never a placeholder "none",
    // which reads as a negative finding ("checked, absent") rather than "not reported".
    const reported = this.data.sideEffects?.length
      ? this.data.sideEffects.map((s) => s.trim()).filter(Boolean)
      : this.medications
          .filter((m) => m.sideEffects)
          .map((m) => `${m.name}: ${m.sideEffects.trim()}`);

    const first = this.isSingleCheck;
    const lines = [
      // Fix 45: first scan → "מעקב ראשון".
      first
        ? this.rv('ehrSourceFirst', { date: this.data.checkDate })
        : this.rv('ehrSource', { visit: this.data.visitNumber, date: this.data.checkDate }),
      // Fix 46: no aggregate "adherence %" here either - it was the mean of 4
      // hand-picked constants, not a rate. The per-supplement category lives on the
      // regimen card.
      this.rv('ehrMeds', { meds }),
    ];
    // Fix 32: severity axis FIRST - every symptom >=7, complete. This is what
    // guarantees a stable 10/10 visit-reason symptom (sleep, urinary) reaches the
    // record; the change axis alone dropped it.
    const prominent = this.prominentSymptoms
      .map((s) => `${s.label} ${s.value}/10`)
      .join(', ');
    if (prominent) lines.push(this.rv('ehrProminent', { prominent }));
    // Change axis: first scan = a single explicit "no comparison yet" note (fix 45),
    // never empty change/resolved slots. Else the worsened rows + resolved-to-0.
    if (first) {
      lines.push(this.rv('ehrFirstScan'));
    } else {
      const changes = this.attentionResponseRows
        .map((r) => `${r.label} ${this.rv('droppedWas', { prev: r.prev, current: r.current })}`)
        .join(', ');
      const resolved = this.resolvedSinceLast.map((r) => r.label).join(', ');
      const segs: string[] = [];
      if (changes) segs.push(this.rv('ehrChanges', { changes }));
      if (resolved) segs.push(this.rv('ehrResolved', { resolved }));
      lines.push(segs.length ? segs.join(' ') : this.rv('ehrNoAttention'));
    }
    // Visit goal on its own line  - avoids a double colon.
    const goal = (this.data.visitGoal || '').trim();
    if (goal) lines.push(this.rv('ehrVisitGoal', { goal }));
    // Fix 54: list reported sensations when present; when none were reported but a
    // regimen exists to report against, say "not reported" (positive framing, mirrors
    // the green card); when there was no sampling context at all, drop the line
    // entirely (rule 45) rather than assert "none".
    if (reported.length) {
      lines.push(this.rv('ehrSideEffectsLine', { sideEffects: reported.join(', ') }));
    } else if (this.medications.length) {
      lines.push(this.rv('ehrNoSideEffectsReported'));
    }
    lines.push(this.rv('ehrInterpret'));
    return lines.join('\n');
  }

  get ringDashOffset(): number {
    const circumference = 213.6;
    return circumference - (circumference * this.adherencePercent) / 100;
  }

  get attentionResponseRows(): Array<{ label: string; color: string; prev: number; current: number; tag: string; tagClass: string }> {
    // Surface the symptoms that most warrant attention: highest current severity,
    // with worsening (positive delta) boosting priority. Mirrors the clinical
    // emphasis of the legacy report (top severe / most-increased symptoms first).
    const symptoms = this.data?.topSymptoms || [];
    if (!symptoms.length) return [];
    return symptoms
      .map((item) => {
        const value = Number(item.value || 0);
        const hasDelta = item.delta !== null && item.delta !== undefined;
        const delta = hasDelta ? Number(item.delta) : 0;
        const worsened = delta > 0;
        return { item, value, hasDelta, delta, worsened, priority: value + (worsened ? delta : 0) };
      })
      // Fix 40: this card is now "עלו מאז המעקב הקודם" (rose since last) - so it only
      // lists symptoms that actually rose, mirroring the green "dropped" card. A
      // severe-but-unchanged symptom lives in the prominent summary + appendix, not here.
      .filter((row) => row.value >= 4 && row.worsened)
      .sort((a, b) => b.priority - a.priority || b.value - a.value)
      .slice(0, 4)
      .map((row) => {
        // Fix 52: this card lists only symptoms that ROSE, so a comparison point
        // always exists (worsened => delta > 0). Expose prev + current and render
        // the same "היה X, כעת Y" format as the green card, instead of "עלה ב-10"
        // which forced the reader to do the subtraction to know from where to where.
        // Tag is derived from the CURRENT value via the unified band (fix 19).
        const previous = Math.max(0, row.value - row.delta);
        const band = this.severityBand(row.value);
        return {
          label: this.symptom(row.item.key),
          color: this.symptomColor(row.item.key),
          prev: previous,
          current: row.value,
          tag: band.label,
          tagClass: band.tagClass,
        };
      });
  }

  get gridRows(): number[] {
    return Array.from({ length: 11 }, (_, i) => i);
  }

  get currentSymptoms(): Record<string, number> {
    const latest = this.timelinePoints[this.timelinePoints.length - 1]?.symptoms;
    if (latest) return latest;
    return Object.fromEntries((this.data?.topSymptoms || []).map((symptom) => [symptom.key, symptom.value]));
  }

  /** Full appendix: EVERY symptom the patient rated > 0, sorted most-severe first,
   *  each with its numeric score and a consistent tier tag. Nothing is filtered
   *  out  - a mid-severity report (e.g. chills 6/10, daytime sleepiness 6/10) can
   *  never silently disappear from the report the way the top-N cards allow. */
  get allRatedSymptoms(): Array<{ label: string; value: number; tier: string; tierClass: string }> {
    const current = this.currentSymptoms;
    return Object.entries(current)
      .map(([key, value]) => ({ key, value: Number(value || 0) }))
      .filter((row) => row.value > 0)
      .sort((a, b) => b.value - a.value)
      .map((row) => {
        // Fix 19: unified severity band (mild 0-4 / moderate 5-6 / pronounced 7-10),
        // identical to the consumer report  - no separate appendix threshold.
        const band = this.severityBand(row.value);
        return {
          label: this.symptom(row.key),
          value: row.value,
          tier: band.label,
          tierClass: band.tagClass,
        };
      });
  }

  /** Fix 61 (round 7): the interpretation-context line, now carrying BOTH halves of
   *  the two fixed pairs (the cross-reference card was removed, fix 59). Half A =
   *  the sleep/fatigue symptoms that may affect performance (fix 50 behaviour, named
   *  + own score). Half B = the self-reported concentration/memory symptoms that
   *  parallel the measured cognitive domains - the piece the doctor most wants next
   *  to the scores. It only PLACES the two data points side by side; it never
   *  interprets, compares, or says "consistent"/"despite" (that would be the verdict
   *  the removed card was rightly stripped of). */
  get cognitiveContextLine(): string {
    const c = this.currentSymptoms;
    const hasSelfModule = Object.keys(c).length > 0;
    // Case 4: no symptom rating this visit.
    if (!hasSelfModule) return `${this.rv('contextLead')} ${this.rv('contextNotCompleted')}`;

    const sleepPair = XREF_PAIRS.find((p) => p.key === 'sleep');
    const cogPair = XREF_PAIRS.find((p) => p.key === 'cognitive');

    // Half A: daytime fatigue + the top sleep-difficulty symptom, each named with its
    // own score, kept to the reported confounders (>=5), same as fix 50.
    const sleepDifficultyKeys = (sleepPair?.selfKeys || []).filter((k) => k !== 'daytime_sleepiness');
    const topSleep = sleepDifficultyKeys
      .map((k) => ({ key: k, value: Number(c[k] || 0) }))
      .sort((a, b) => b.value - a.value)[0];
    const halfACand = [{ key: 'daytime_sleepiness', value: Number(c['daytime_sleepiness'] || 0) }, topSleep]
      .filter((r) => r && r.value >= 5)
      .sort((a, b) => b.value - a.value);
    const halfA = this.joinList(halfACand.map((r) => this.rv('confounderItem', { symptom: this.symptom(r.key), value: r.value })));

    // Half B: the self-reported concentration/memory symptoms. List those rated >0;
    // when all are 0, collapse to "the four concentration symptoms 0/10".
    const concKeys = cogPair?.selfKeys || [];
    const concRated = concKeys
      .map((k) => ({ key: k, value: Number(c[k] || 0) }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value);
    const halfB = concRated.length
      ? this.joinList(concRated.map((r) => this.rv('confounderItem', { symptom: this.symptom(r.key), value: r.value })))
      : this.rv('contextConcAllZero');

    // Compose the two halves (half B always exists; half A only when a confounder
    // is present).
    let body: string;
    if (halfA && halfB) body = this.rv('contextBoth', { a: halfA, b: halfB });
    else if (halfB) body = this.rv('contextBOnly', { b: halfB });
    else body = this.rv('contextAOnly', { a: halfA });

    // Case 3: no cognitive sample taken - state it after the two halves.
    const hasCognitiveSample = this.cognitiveRows.some((r) => !r.notMeasured);
    if (!hasCognitiveSample) body = `${body} ${this.rv('contextNoSample')}`;

    return `${this.rv('contextLead')} ${body}`;
  }

  /** Join a list with the locale's "and" (Hebrew vav-prefix, no space before the
   *  last item; English " and "). Used by the interpretation-context line (fix 50). */
  private joinList(items: string[]): string {
    if (items.length <= 1) return items[0] || '';
    const head = items.slice(0, -1).join(', ');
    const last = items[items.length - 1];
    return this.translation.getCurrentLanguage() === 'he' ? `${head} ו${last}` : `${head} and ${last}`;
  }

  symptom(key: string): string {
    if (!key) return '';
    return this.tx(`doctorReport.symptoms.${key}`, key.replace(/_/g, ' '));
  }

  /**
   * SENSE (vision + hearing) provider block. Context, not a finding. CHANGE before
   * VALUE, no population comparison, a stable metric is SHOWN, "not measured" is
   * explicit (never a 0 / dash / empty cell). The provider surface MAY name a
   * direction (improvement / decline + dB) - deliberately different from the user
   * screen. Direction uses the shared inverted-sign better() (logCS high = better,
   * SNR low = better). (Handoff 04.)
   */
  get senseBlock(): SenseBlockVM | null {
    const scans: any[] = this.data?.sense?.scans || [];
    if (!scans.length) return null;
    const current = scans[scans.length - 1];
    const prior = scans.slice(0, scans.length - 1);
    const firstScan = prior.length === 0;
    // Through tv(), not translate(), so the WHOLE sense namespace follows the
    // reader's voice the way the rest of the report does: one key here already
    // talks about her ("she also reported"), and the next one added would too.
    const t = (k: string, p?: Record<string, unknown>) =>
      this.tv(`doctorReport.sense.${k}`, p);
    const rawVal = (metric: SenseMetric, v: number) =>
      metric === 'vision' ? `logCS ${v.toFixed(2)}` : `SNR ${v.toFixed(1)} dB`;
    const metrics: { metric: SenseMetric; labelKey: string }[] = [
      { metric: 'vision', labelKey: 'doctorReport.sense.contrast' },
      { metric: 'hearing', labelKey: 'doctorReport.sense.tone' },
    ];

    const rows: SenseReportRow[] = metrics.map(({ metric, labelKey }) => {
      const cur = metricValue(current, metric);
      let prevRec: any = null;
      for (let i = prior.length - 1; i >= 0; i--) {
        if (metricValue(prior[i], metric) != null) {
          prevRec = prior[i];
          break;
        }
      }
      const prev = metricValue(prevRec, metric);
      const currentValue = cur != null ? rawVal(metric, cur) : t('notMeasured');
      const prevRaw =
        prev != null
          ? metric === 'vision'
            ? prev.toFixed(2)
            : `${prev.toFixed(1)}`
          : t('notMeasured');

      if (firstScan) {
        return {
          metric,
          labelKey,
          changeText: '',
          currentValue,
          prevValue: '',
          note: cur != null ? t('firstScanNote') : t('notCompletedNote'),
        };
      }
      if (cur == null || prev == null) {
        return {
          metric,
          labelKey,
          changeText: t('notMeasured'),
          currentValue,
          prevValue: prevRaw,
          note: '',
        };
      }
      const change = computeChange(metric, current, prevRec);
      let changeText = t('stable');
      if (change.state === 'improved' || change.state === 'declined') {
        const word =
          change.state === 'improved' ? t('improved') : t('declined');
        const mag =
          metric === 'hearing'
            ? `${Math.round(Math.abs(change.delta as number))} dB`
            : `${Math.abs(change.delta as number).toFixed(2)} logCS`;
        changeText = `${word}, ${mag}`;
      }
      return { metric, labelKey, changeText, currentValue, prevValue: prevRaw, note: '' };
    });

    const prevScan = prior.length ? prior[prior.length - 1] : null;
    const sinceDate = prevScan?.taken_at
      ? this.formatShortDate(prevScan.taken_at)
      : '';
    const summary = firstScan
      ? t('firstScanSummary')
      : t('scanSummary', {
          count: scans.length,
          firstDate: scans[0]?.taken_at ? this.formatShortDate(scans[0].taken_at) : '',
          thisDate: current?.taken_at ? this.formatShortDate(current.taken_at) : '',
        });

    // Cross-link to reported sensory symptoms - no causal claim.
    const cs = this.currentSymptoms;
    const items = ['vision_fluctuation', 'tinnitus_ringing', 'noise_sensitivity', 'light_sensitivity']
      .filter((k) => (cs[k] || 0) > 0)
      .map((k) => `${this.symptom(k)} (${cs[k]}/10)`);
    const alsoReported = items.length ? t('alsoReported', { list: items.join(', ') }) : '';

    return { firstScan, rows, sinceDate, summary, alsoReported };
  }

  signal(key: string): string {
    if (!key) return '';
    return this.tx(`doctorReport.reportView.v4.signal.${key}`, key.replace(/_/g, ' '));
  }

  /** Fix 24: cognitive domain title / subtitle / description, pulled from the SAME
   *  results.th.tasks.* source the patient's THINK screen uses, so the six names are
   *  identical in both reports. Legacy signal keys (no domain mapping) fall back to
   *  v4.signal for the title and render no subtitle/description. */
  private cognitiveDomainLabel(key: string, field: 'title' | 'subtitle' | 'what'): string {
    const domainKey = COGNITIVE_DOMAIN_KEY[key];
    if (domainKey) {
      // Fix 26: the DESCRIPTION ("what") uses provider-specific impersonal strings
      // (v4.cogWhat.*), NOT the patient-facing results.th.tasks.*.what which is
      // written in 2nd person ("how well you adapt..."). Title + subtitle stay on
      // results.th.tasks so the domain NAMES remain identical across both reports.
      const path =
        field === 'what'
          ? `doctorReport.reportView.v4.cogWhat.${domainKey}`
          : `results.th.tasks.${domainKey}.${field}`;
      const value = this.translation.translate(path);
      if (value && value !== path) return value;
    }
    return field === 'title' ? this.signal(key) : '';
  }

  cluster(key: string): string {
    if (!key) return '';
    return this.tx(`doctorReport.reportView.v4.cluster.${key}`, key.replace(/_/g, ' '));
  }

  /** "N תסמינים" with correct Hebrew singular for a one-symptom cluster. */
  clusterCountText(n: number): string {
    return n === 1 ? this.rv('clusterCountOne') : this.rv('clusterCount', { n });
  }

  /** Fix 64 (round 8): the follow-up cluster header counts the rows SHOWN (after the
   *  fix-62 filter), not the catalog. "N מוצגים" / "N shown", never "X of Y". */
  clusterShownText(n: number): string {
    return n === 1 ? this.rv('clusterShownOne') : this.rv('clusterShown', { n });
  }

  /** Fix 66 (round 8): after fix 62 hides fully-zero-both clusters, name them ONCE
   *  at the foot of the follow-up card. Up to 4 -> named; 5 and over -> a count that
   *  points to the appendix, so the line never becomes a taxonomy-reciting paragraph
   *  (the exact noise fix 47 removed, now re-checked against the grown 20-cluster
   *  taxonomy). Derived from the same fix-62 filter, so the count always equals the
   *  clusters actually hidden. Follow-up only; the first-scan card has no "both
   *  check-ins" and declares nothing zero. */
  get zeroedClustersLine(): string {
    if (!this.hasTrends || !this.timelinePoints.length) return '';
    const cur = this.currentSymptoms;
    const zeroed = CLUSTER_VIEW_DEFS.filter(
      (def) =>
        !def.symptomKeys.some((k) => {
          const curr = Number(cur[k] || 0);
          const prv = this.previousRatedValue(k);
          return curr > 0 || (prv !== null && prv > 0);
        }),
    );
    if (!zeroed.length) return '';
    if (zeroed.length <= 4) {
      const names = zeroed.map((def) => this.cluster(def.key)).join(', ');
      return this.rv('clustersZeroBothNamed', { names });
    }
    return this.rv('clustersZeroBothCount', { count: zeroed.length });
  }

  /** Fix 19: the one severity band a value falls in (mild / moderate / pronounced),
   *  with the shared label + a css tag class. Drives both the appendix and the
   *  "needs attention" chips. */
  severityBand(value: number): { key: string; label: string; tagClass: string } {
    const band =
      SEVERITY_BANDS.find((b) => value <= b.max) ||
      SEVERITY_BANDS[SEVERITY_BANDS.length - 1];
    const label = this.rv('band' + band.key.charAt(0).toUpperCase() + band.key.slice(1));
    const tagClass = band.key === 'high' ? 'tag-high' : band.key === 'mid' ? 'tag-mod' : 'tag-low';
    return { key: band.key, label, tagClass };
  }

  /** Fix 18: direction-safe, current-first change text. The old "prev → curr" form
   *  flips visually in the un-isolated RTL EHR text ("0/10 → 10/10" renders as
   *  "10/10 → 0/10"), making a worsening read as resolved. Current value leads and
   *  the direction is a WORD, so it can never reverse. A missing comparison point →
   *  just the current value, never "→ 0". */
  changeDisplay(current: number, previous: number | null | undefined): string {
    if (previous === null || previous === undefined) return `${current}/10`;
    const diff = current - previous;
    if (diff > 0) return this.rv('changeUp', { value: current, delta: diff });
    if (diff < 0) return this.rv('changeDown', { value: current, delta: -diff });
    return `${current}/10`;
  }

  bleedingPattern(pattern: string): string {
    return this.tx(`doctorReport.reportView.v4.bleeding.${pattern}`, pattern.replace(/_/g, ' '));
  }

  formatBleedingPatterns(patterns: string[]): string {
    return (patterns || []).map((pattern) => this.bleedingPattern(pattern)).join(', ');
  }

  valuePercent(value: number, max = 100): number {
    const safe = Math.max(0, Math.min(max, Number(value) || 0));
    return Math.round((safe / max) * 100);
  }

  /**
   * The ONE date convention the whole report speaks: DD.MM.YYYY, day first, the
   * same form the server stamps on checkDate and the masthead has always shown.
   * toLocaleDateString prints M/D/YYYY in English, so a 3 January start date read
   * "3/1/2026" directly above an axis printing that same day as "03.01" - two ways
   * to say one date on one clinician's screen, and "3/1" alone is unresolvable.
   * Hebrew is unchanged in substance (he-IL is day first too), only zero padded.
   */
  reportDate(dateStr?: string): string {
    const ts = this.parseReportDate(dateStr);
    if (ts === null) return dateStr || '';
    const d = new Date(ts);
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()}`;
  }

  formatFrequency(freq: string, custom?: string): string {
    if (freq === 'other') return custom || this.rv('freq.other');
    return freq ? this.tx(`doctorReport.reportView.v4.freq.${freq}`, freq) : '';
  }

  formatConsistency(consistency: string): string {
    if (!consistency) return this.rv('consistency.unknown');
    return this.tx(`doctorReport.reportView.v4.consistency.${consistency}`, consistency);
  }

  signalScore(signal: ReportSignalEntry): number {
    const numeric = Number(String(signal.value).replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(numeric)) return 0;
    if (signal.key === 'reactionTime') return Math.max(0, Math.min(100, 100 - numeric / 10));
    return Math.max(0, Math.min(100, numeric));
  }

  xForWeek(week: number): number {
    return this.chartLeft + (Math.max(0, week) / this.timelineMaxWeek) * this.chartInnerWidth;
  }

  xForScanNumber(scanNumber: number): number {
    const max = this.timelineMaxScanNumber;
    if (max <= 1) return this.chartLeft;
    return this.chartLeft + ((Math.max(1, scanNumber) - 1) / (max - 1)) * this.chartInnerWidth;
  }

  /** Fix 29a: the rated scans' timestamps that anchor the DATE x-axis. */
  private get ratedTimes(): number[] {
    return this.ratedTimelinePoints
      .map((p) => new Date(p.date).getTime())
      .filter((t) => Number.isFinite(t));
  }

  /** Fix 29a: position on a real date axis (rated scans only), so points sit at
   *  their true time spacing instead of a compressed 1..61 index. */
  xForDate(dateStr: string): number {
    const times = this.ratedTimes;
    if (!times.length) return this.chartLeft;
    const min = Math.min(...times);
    const max = Math.max(...times);
    if (max <= min) return this.chartLeft + this.chartInnerWidth / 2;
    const t = new Date(dateStr).getTime();
    const clamped = Math.max(min, Math.min(max, Number.isFinite(t) ? t : min));
    return this.chartLeft + ((clamped - min) / (max - min)) * this.chartInnerWidth;
  }

  /** Fix 29a: one axis tick per rated scan, labelled with its DATE (not a scan-
   *  number index). Labels stagger on two rows so close dates don't overlap. (Fix
   *  38b's wider stagger/edge-anchor was cancelled by fix 42  - the graph returns to
   *  its production form.) */
  get dateAxisTicks(): Array<{ label: string; x: number; y: number; anchor: string }> {
    const pts = this.ratedTimelinePoints;
    // When two rated scans fall on the SAME calendar day, the date alone would
    // print twice; disambiguate those with the time so they read as two checks.
    const dayCount = new Map<string, number>();
    pts.forEach((p) => {
      const d = this.formatShortDate(p.date);
      dayCount.set(d, (dayCount.get(d) || 0) + 1);
    });
    const right = this.chartLeft + this.chartInnerWidth;
    return pts.map((p, i) => {
      const day = this.formatShortDate(p.date);
      const x = this.xForDate(p.date);
      return {
        label: (dayCount.get(day) || 0) > 1 ? this.formatDayTime(p.date) : day,
        x,
        y: 286 + (i % 2) * 10,
        // A centred label on the last tick hangs past the right edge of the drawing
        // and the SVG clips it, so the newest date - the one a clinician looks for
        // first - lost its last characters. The outermost ticks anchor inwards.
        anchor: x >= right - 1 ? 'end' : x <= this.chartLeft + 1 ? 'start' : 'middle',
      };
    });
  }

  /** Compact "dd.mm HH:MM" for same-day scans that would otherwise share a label. */
  private formatDayTime(dateStr: string): string {
    const ts = this.parseReportDate(dateStr);
    if (ts === null) return dateStr || '';
    const d = new Date(ts);
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${p2(d.getDate())}.${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
  }

  /** The axis label: the same day-first date as everywhere else, two digit year
   *  because that is all the tick has room for. */
  private formatShortDate(dateStr: string): string {
    const ts = this.parseReportDate(dateStr);
    if (ts === null) return dateStr || '';
    const d = new Date(ts);
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)}`;
  }

  xForEventWeek(week: number): number {
    const rated = this.ratedTimelinePoints;
    if (!rated.length) return this.xForWeek(week);
    // Anchor the therapy marker to the nearest rated scan's date on the date axis.
    const nearest = rated.reduce(
      (best, point) =>
        Math.abs((point.week || 0) - week) < Math.abs((best.week || 0) - week) ? point : best,
      rated[0],
    );
    return this.xForDate(nearest.date);
  }

  yForScore(value: number): number {
    return this.chartTop + this.chartInnerHeight - (Math.max(0, Math.min(10, value)) / 10) * this.chartInnerHeight;
  }

  /**
   * The summary a clinician pastes into the chart, as a dialog she opens from the
   * record rather than a block sitting open in the middle of the report. The text
   * is editable and the EDIT is what gets copied - the old inline box was editable
   * too but copyEhr always sent the generated text, so a correction made in the box
   * was silently dropped on the way to the clipboard.
   */
  @ViewChild('ehrDialog') private ehrDialogRef?: ElementRef<HTMLDialogElement>;
  /** null until she types: the summary regenerates with the data until then. */
  ehrDraft: string | null = null;

  get ehrText(): string {
    return this.ehrDraft ?? this.ehrSummary;
  }

  /** Reset only makes sense once the text really differs from the generated summary. */
  get ehrEdited(): boolean {
    return this.ehrDraft !== null && this.ehrDraft !== this.ehrSummary;
  }

  openEhrDialog(): void {
    const el = this.ehrDialogRef?.nativeElement;
    if (!el) return;
    if (typeof el.showModal === 'function') el.showModal();
    else el.setAttribute('open', '');
  }

  closeEhrDialog(): void {
    const el = this.ehrDialogRef?.nativeElement;
    if (!el) return;
    if (typeof el.close === 'function') el.close();
    else el.removeAttribute('open');
  }

  onEhrInput(value: string): void {
    this.ehrDraft = value;
  }

  /** Back to what the mapping says, discarding her edits. */
  resetEhrDraft(): void {
    this.ehrDraft = null;
  }

  copyEhr(): void {
    const text = this.ehrText;
    navigator.clipboard?.writeText(text).then(
      () => {
        this.copyState = this.rv('copied');
        setTimeout(() => (this.copyState = ''), 1600);
      },
      () => this.fallbackCopy(text)
    );
  }

  /** Fix 31: the print button now opens the browser's native print dialog
   *  (Save-as-PDF or paper) instead of the server puppeteer PDF that silently did
   *  nothing when it failed. window.print() is instant, gives immediate feedback
   *  (the dialog), and honours the @media print rules. If a WebView blocks it, tell
   *  the user they can save the page as a PDF rather than leaving a dead button. */
  printReport(): void {
    if (typeof window === 'undefined' || typeof window.print !== 'function') {
      alert(this.rv('printUnavailable'));
      return;
    }
    try {
      // A closed <details> prints as its summary alone, whatever the print
      // stylesheet says, so the folded blocks are opened before the dialog and
      // put back exactly as they were afterwards.
      const folded = Array.from(
        document.querySelectorAll<HTMLDetailsElement>('.report-v5 details')
      ).filter((el) => !el.open);
      folded.forEach((el) => (el.open = true));
      const restore = () => folded.forEach((el) => (el.open = false));
      window.addEventListener('afterprint', restore, { once: true });
      // Mobile browsers that are not Safari (Chrome / Edge / Firefox on iOS,
      // Samsung Internet) define window.print but do NOTHING when it is called,
      // so the button looked dead. Nothing throws, so the only signal is that no
      // print dialog ever opened: neither `beforeprint` nor the `print` media
      // query fired. Watch for both and, if neither happened, say how to print
      // from that browser instead of leaving her tapping a silent button.
      let dialogOpened = false;
      const seen = () => (dialogOpened = true);
      const mq = window.matchMedia ? window.matchMedia('print') : null;
      const onMedia = (event: MediaQueryListEvent) => {
        if (event.matches) dialogOpened = true;
      };
      window.addEventListener('beforeprint', seen);
      mq?.addEventListener?.('change', onMedia);
      this.printHint = '';
      window.print();
      setTimeout(() => {
        window.removeEventListener('beforeprint', seen);
        mq?.removeEventListener?.('change', onMedia);
        if (dialogOpened) return;
        restore();
        // No dialog: the browser simply cannot print. Have the server render
        // the PDF instead and open it, so the button still ends in a PDF.
        void this.openServerPdf();
      }, 1200);
    } catch (error) {
      console.error('print failed:', error);
      alert(this.rv('printUnavailable'));
    }
  }

  /**
   * The fallback for the browsers that ignore window.print(): the server renders
   * the same report to a PDF and we open that URL. A plain navigation works
   * everywhere the print dialog does not (the app's WebView, iOS Chrome / Edge /
   * Firefox, Samsung Internet), and from the PDF the phone's own share sheet can
   * save or send it. A blocked pop-up is not a dead end: the same URL is then
   * opened in this tab, and the report is still there on back.
   */
  private async openServerPdf(): Promise<void> {
    if (this.printBusy) return;
    if (!this.scanId || this.isProviderPreview) {
      this.printHint = this.rv('printNoDialog');
      return;
    }
    this.printBusy = true;
    this.printHint = this.rv('printPreparing');
    try {
      const url = await this.doctorReportService.createPdfUrl(this.scanId);
      const opened = window.open(url, '_blank');
      if (!opened) {
        // Pop-up blocked: navigate this tab instead, the report is still here
        // when she comes back.
        window.location.href = url;
        return;
      }
      this.printHint = '';
    } catch (error) {
      console.error('server pdf failed:', error);
      this.printHint = this.rv('printNoDialog');
    } finally {
      this.printBusy = false;
    }
  }

  goBack(): void {
    const state = history.state as PatientListState;
    if (this.isMaccabiRoute || state?.fromMaccabi) {
      this.router.navigate(this.maccabiDoctorPortalRoute('patients'));
      return;
    }
    this.router.navigate(['/app/doctor-report', this.scanId]);
  }

  isHrt(m: MedicationEntry): boolean {
    const value = `${m.name || ''} ${m.reason || ''}`.toLowerCase();
    return /(hrt|evorel|mirena|estro|prog|אוטרוגסטן|אסטר|מירנה|הורמ)/i.test(value);
  }


  // ---------------------------------------------------------------------------
  // The record layout (the clinical-eval design, markable-clinical-eval.web.app).
  // One document: an identity masthead, then three tabs on screen. Print shows
  // every tab, so a printed report is still the whole record in one run.
  // ---------------------------------------------------------------------------

  activeTab: ReportTab = 'summary';

  setTab(tab: ReportTab): void {
    this.activeTab = tab;
  }

  /** Translate a doctorReport.reportView.v5.* key, in the reader's voice. */
  rv5(key: string, params?: Record<string, unknown>): string {
    return this.voiced('v5', key, params);
  }

  readonly recordTabs: Array<{ id: ReportTab; index: string; labelKey: string }> = [
    { id: 'summary', index: '01', labelKey: 'tabSummary' },
    { id: 'timeline', index: '02', labelKey: 'tabTimeline' },
    { id: 'detail', index: '03', labelKey: 'tabExplorer' },
  ];

  /** Up to two initials for the identity avatar. Falls back to a single letter. */
  get patientInitials(): string {
    const name = (this.data?.patientName || '').trim();
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    const letters = parts.slice(0, 2).map((p) => p[0] || '');
    return letters.join('').toUpperCase() || name[0].toUpperCase();
  }

  /** How many mappings are behind this report (symptom-bearing scans). */
  get mappingCount(): number {
    return this.checkCount;
  }

  get mappingCountText(): string {
    const n = this.mappingCount;
    return n === 1 ? this.rv5('mappingsOne') : this.rv5('mappingsMany', { n });
  }

  /**
   * The severity spread of this mapping, the same four counts the design opens
   * the symptom summary with. Only rated symptoms are counted; a symptom she was
   * never asked about is absent, not a zero.
   */
  get digestCounts(): {
    positive: number;
    high: number;
    moderate: number;
    low: number;
  } {
    const rated = this.allRatedSymptoms;
    return {
      positive: rated.length,
      high: rated.filter((r) => r.value >= 6).length,
      moderate: rated.filter((r) => r.value >= 3 && r.value <= 5).length,
      low: rated.filter((r) => r.value >= 1 && r.value <= 2).length,
    };
  }

  /**
   * The three she said she most wants to improve, in her order, each with the
   * rating she gave it on this mapping. A symptom she did not rate shows the
   * name alone rather than a fabricated number.
   */
  get priorityItems(): Array<{ label: string; value: number | null }> {
    const keys = this.data?.concerns || [];
    const current = this.currentSymptoms;
    return keys
      .filter((key) => !!key)
      .map((key) => {
        const raw = current[key];
        const value = typeof raw === 'number' && raw > 0 ? raw : null;
        return { label: this.symptom(key), value };
      });
  }

  /** Her own words from the visit-goal question, or nothing. */
  get visitQuote(): string {
    return (this.data?.visitGoal || '').trim();
  }

  /** The symptoms that came DOWN against the chosen window, biggest first. */
  get lowerChanges(): Array<{
    label: string;
    prev: number;
    now: number;
    delta: number;
  }> {
    const base = this.comparisonMode === 'treatment' && this.canCompareTreatment
      ? this.treatmentBasePoint
      : null;
    if (base) {
      return this.directionRowsAgainst(base)
        .filter((r) => r.delta < 0)
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || b.now - a.now);
    }
    return this.droppedSinceLast
      .map((r) => ({
        label: r.label,
        prev: r.prev,
        now: r.current,
        delta: r.current - r.prev,
      }))
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || b.now - a.now);
  }

  /** The symptoms that went UP against the chosen window, biggest first. */
  get higherChanges(): Array<{
    label: string;
    prev: number;
    now: number;
    delta: number;
  }> {
    const base = this.comparisonMode === 'treatment' && this.canCompareTreatment
      ? this.treatmentBasePoint
      : null;
    if (base) {
      return this.directionRowsAgainst(base)
        .filter((r) => r.delta > 0)
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || b.now - a.now);
    }
    return this.attentionResponseRows
      .map((r) => ({
        label: r.label,
        prev: r.prev,
        now: r.current,
        delta: r.current - r.prev,
      }))
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || b.now - a.now);
  }

  /** Written with an explicit sign so a printed row reads on its own. */
  deltaText(delta: number): string {
    if (delta > 0) return `+${delta}`;
    if (delta < 0) return `−${Math.abs(delta)}`;
    return '0';
  }

  /**
   * Everything of her background that we actually collect, as label/value rows.
   * A question she was never asked produces NO row at all: the design's other
   * rows (family history, BMI, blood pressure, screening) are not displayed
   * because we do not ask them, and an empty row would read as a denial.
   */
  get historyRows(): Array<{ label: string; value: string }> {
    const rows: Array<{ label: string; value: string }> = [];
    const d = this.data;
    if (!d) return rows;
    const push = (label: string, value: string | null | undefined) => {
      const text = (value || '').trim();
      if (text) rows.push({ label, value: text });
    };

    // Cycle and bleeding, as she answered them on the two questionnaires.
    const intake = d.intake;
    if (intake?.periodLast12) {
      push(
        this.rv5('histPeriod'),
        this.rv5(intake.periodLast12 === 'yes' ? 'periodYes' : 'periodNo')
      );
    }
    if (intake?.lastPeriod) {
      push(
        this.rv5('histLastPeriod'),
        this.tx(
          `symptoms.lastPeriod.${LAST_PERIOD_LABEL[intake.lastPeriod] || intake.lastPeriod}`,
          intake.lastPeriod.replace(/_/g, ' ')
        )
      );
    }
    if (intake?.contraception) {
      push(
        this.rv5('histContraception'),
        this.rv5(
          intake.contraception === 'yes' ? 'contraceptionYes' : 'contraceptionNo'
        )
      );
    }
    if (intake?.noBleedReasons?.length) {
      push(
        this.rv5('histNoBleedReason'),
        intake.noBleedReasons
          .map((r) =>
            this.tx(`symptoms.noBleedReasons.${r}`, r.replace(/_/g, ' '))
          )
          .join(', ')
      );
    }
    push(this.rv5('histBleedingPattern'), this.bleedingPatternText);
    push(this.rv5('histBleedingFlags'), this.bleedingFlagText);
    push(this.rv5('histProcedure'), this.surgeryText);

    // What she tried before, and what she leaves untreated.
    const prior = d.priorHormoneTherapy;
    if (prior && prior.tried !== null && prior.tried !== undefined) {
      const parts: string[] = [
        this.rv5(prior.tried ? 'priorTried' : 'priorNotTried'),
      ];
      if (prior.tried && prior.types?.length) {
        parts.push(
          prior.types
            .map((t) => this.tx(`doctorReport.priorHormone.type_${t}`, t))
            .join(', ')
        );
      }
      if (prior.tried && prior.stopReason?.trim()) {
        parts.push(this.rv5('priorStopped', { reason: prior.stopReason.trim() }));
      }
      push(this.rv5('histPriorTherapy'), parts.join(' · '));
    }
    const untreated = d.untreatedCondition;
    if (untreated && untreated.has === true && untreated.detail?.trim()) {
      push(this.rv5('histUntreated'), untreated.detail.trim());
    }

    // Allergies come from the symptom mapping, not from a drug-allergy list:
    // what she rated is "new or stronger allergies", plus the follow-up asking
    // whether she had them before.
    push(this.rv5('histAllergies'), this.allergyText);

    // Her everyday pattern, localised from her raw answers rather than from the
    // server's English clinician lines, so the Hebrew report reads in Hebrew.
    for (const row of this.lifestyleRows) rows.push(row);
    return rows;
  }


  /**
   * Every rated symptom with the value she gave it at the previous mapping, so
   * the full table can show now, before and the direction. A symptom with no
   * paired value at the earlier mapping shows the current rating alone.
   */
  get ratingTableRows(): Array<{
    label: string;
    now: number;
    prev: number | null;
    delta: number | null;
  }> {
    const points = this.timelinePoints;
    const previous =
      points.length > 1 ? points[points.length - 2]?.symptoms || {} : {};
    const current = this.currentSymptoms;
    return Object.entries(current)
      .map(([key, value]) => ({ key, value: Number(value || 0) }))
      .filter((row) => row.value > 0)
      .sort((a, b) => b.value - a.value)
      .map((row) => {
        const raw = previous[row.key];
        const prev = typeof raw === 'number' ? raw : null;
        return {
          label: this.symptom(row.key),
          now: row.value,
          prev,
          delta: prev === null ? null : row.value - prev,
        };
      });
  }


  // ---------------------------------------------------------------------------
  // TIMELINE PANEL. The chart is hers to compose: she picks which symptoms are
  // drawn, and the treatment periods she reported are shaded behind them.
  // ---------------------------------------------------------------------------

  /**
   * Which symptom series are drawn. null means the default set (the tracked
   * symptoms plus her most prominent ones), so the chart is never empty on
   * arrival; once she touches the picker the choice is hers.
   */
  selectedSeriesKeys: Set<string> | null = null;
  /** Layer toggles, mirroring the design's Layers switches. */
  showSymptomLayer = true;
  showTreatmentLayer = true;
  /** null = untouched, so the layer follows the record: on when she reported
   *  effects, off when she reported none. Her own click wins from then on. */
  private sideEffectLayerChoice: boolean | null = null;

  get showSideEffectLayer(): boolean {
    return this.sideEffectLayerChoice ?? this.hasSideEffectEvents;
  }

  toggleSideEffectLayer(): void {
    this.sideEffectLayerChoice = !this.showSideEffectLayer;
  }

  /** The side effects she wrote on her mappings, each on its own date. */
  get sideEffectEvents(): ReportSideEffectEvent[] {
    return this.data?.sideEffectEvents || [];
  }

  get hasSideEffectEvents(): boolean {
    return this.sideEffectEvents.length > 0;
  }

  /**
   * Those same effects placed on the chart: a diamond on the date she reported
   * it, with her wording beside it. Two effects on the same mapping share an x,
   * so the labels alternate above and below to stay readable.
   */
  get sideEffectMarks(): Array<{
    x: number;
    y: number;
    labelX: number;
    labelY: number;
    anchor: 'middle';
    label: string;
    title: string;
  }> {
    if (!this.showSideEffectLayer) return [];
    // The label is centred over its diamond and pulled back inside the plot near
    // the edges. Centring keeps it correct in Hebrew too, where start and end
    // swap sides and an edge label was running off the chart.
    const left = this.chartLeft + 48;
    const right = this.chartLeft + this.chartInnerWidth - 48;
    return this.sideEffectEvents.map((event, index) => {
      const x = this.xForDate(event.date);
      const y = this.chartTop + 30 + (index % 2) * 34;
      return {
        x,
        y,
        labelX: Math.max(left, Math.min(right, x)),
        labelY: y - 13,
        anchor: 'middle' as const,
        label: event.label.length > 28 ? `${event.label.slice(0, 27)}…` : event.label,
        title: [event.medication, event.label, this.reportDate(event.date)]
          .filter(Boolean)
          .join(' · '),
      };
    });
  }

  // ---------------------------------------------------------------------------
  // ADDITIONAL TRENDS. What the timeline chart cannot hold: her overall symptom
  // total, and the mapping domains, each read on its own scale and never mixed
  // into the 0-10 symptom axis. Everything here is her own recorded history - a
  // mapping that did not measure something leaves a gap rather than a line.
  // ---------------------------------------------------------------------------

  /** One mini chart: a few points on their own scale, with the two end dates. */
  private buildMiniChart(
    values: Array<number | null>,
    dates: string[],
    max?: number,
  ): MiniChart {
    const known = values.filter((v): v is number => typeof v === 'number');
    if (!known.length || !dates.length) {
      return { empty: true, segments: [], dots: [], firstLabel: '', lastLabel: '' };
    }
    const low = max ? 0 : Math.min(...known);
    const high = max ?? Math.max(...known);
    const range = high - low || 1;
    const firstTime = this.parseReportDate(dates[0]) ?? 0;
    const lastTime = this.parseReportDate(dates[dates.length - 1]) ?? firstTime;
    const span = lastTime - firstTime || 1;
    const x = (index: number) => {
      if (dates.length < 2) return 210;
      const t = ((this.parseReportDate(dates[index]) ?? firstTime) - firstTime) / span;
      return 40 + t * 340;
    };
    // A single reading, or a series that never moved, has no range of its own.
    // Mapping it with the formula pins it to the floor of the box and leaves a
    // tall empty sky above it; it belongs on the middle line instead.
    const flat = high === low;
    const y = (value: number) =>
      flat ? 39 : 12 + 54 * (1 - (value - low) / range);

    const segments: string[] = [];
    const dots: MiniChart['dots'] = [];
    let run: string[] = [];
    values.forEach((value, index) => {
      if (typeof value !== 'number') {
        if (run.length > 1) segments.push(run.join(' '));
        run = [];
        return;
      }
      const px = x(index);
      const py = y(value);
      run.push(px + ',' + py);
      dots.push({
        x: px,
        y: py,
        title: this.formatShortDate(dates[index]) + ': ' + value,
      });
    });
    if (run.length > 1) segments.push(run.join(' '));

    return {
      empty: false,
      segments,
      dots,
      firstLabel: this.formatShortDate(dates[0]),
      lastLabel: this.formatShortDate(dates[dates.length - 1]),
    };
  }

  /** The value as it reads, or the plain "not measured" rather than a blank. */
  private trendValueText(value: number | null, suffix = ''): string {
    return typeof value === 'number'
      ? value + suffix
      : this.tx('doctorReport.reportView.v4.notMeasured', 'Not measured');
  }

  /** How many of the most recent mappings a values line writes out in full. */
  private static readonly TREND_VALUES_SHOWN = 12;

  /**
   * The two ends of a series, read over what was MEASURED (Danna, 17.09). Her
   * account carries 61 mappings with a task measured in one of them, and
   * "not measured to 100" read the ends of the calendar, not of the measurement.
   */
  private trendRange(series: Array<number | null>): { firstText: string; lastText: string } {
    const known = series.filter((value): value is number => typeof value === 'number');
    if (!known.length) return { firstText: this.trendValueText(null), lastText: '' };
    if (known.length === 1) return { firstText: this.trendValueText(known[0]), lastText: '' };
    return {
      firstText: this.trendValueText(known[0]),
      lastText: this.trendValueText(known[known.length - 1]),
    };
  }

  /**
   * The values line. It used to print one "not measured" per mapping, which
   * filled the card with sixty of them. Only the mappings that recorded
   * something are written, each with its date, and anything older than the
   * last dozen is counted rather than listed.
   */
  private trendValuesText(
    series: Array<number | null>,
    dates: string[],
    suffix = '',
  ): string {
    const measured = series
      .map((value, index) => ({ value, date: dates[index] }))
      .filter((point): point is { value: number; date: string } => typeof point.value === 'number');
    if (!measured.length) return this.trendValueText(null);
    const shown = measured.slice(-ReportViewComponent.TREND_VALUES_SHOWN);
    const hidden = measured.length - shown.length;
    const line = shown
      .map((point) => this.formatShortDate(point.date) + ': ' + point.value + suffix)
      .join(' \u00b7 ');
    return hidden > 0 ? this.rv5('trendMoreValues', { n: hidden }) + ' \u00b7 ' + line : line;
  }

  /**
   * Her symptom total per mapping: the sum of the 0-10 ratings of the SAME set
   * of symptoms the latest mapping recorded. A mapping missing any of them has
   * no total, because a smaller set would read as an improvement it never was.
   */
  get symptomTotalRows(): Array<{ date: string; dateText: string; total: number | null; count: number }> {
    const points = this.timelinePoints.filter(
      (point) => Object.keys(point.symptoms || {}).length > 0,
    );
    if (!points.length) return [];
    const keys = Object.keys(points[points.length - 1].symptoms || {});
    return points.map((point) => {
      const rated = keys.filter(
        (key) => typeof point.symptoms?.[key] === 'number',
      );
      const complete = rated.length === keys.length;
      return {
        date: point.date,
        dateText: this.formatShortDate(point.date),
        total: complete
          ? Math.round(rated.reduce((sum, key) => sum + Number(point.symptoms[key]), 0))
          : null,
        count: rated.length,
      };
    });
  }

  get symptomTotalChart(): MiniChart {
    const rows = this.symptomTotalRows;
    return this.buildMiniChart(
      rows.map((row) => row.total),
      rows.map((row) => row.date),
    );
  }

  get symptomTotalLatest(): string {
    const rows = this.symptomTotalRows;
    return this.trendValueText(rows.length ? rows[rows.length - 1].total : null);
  }

  /** How the total moved since her first mapping, or that the history is incomplete. */
  get symptomTotalMovement(): string {
    const rows = this.symptomTotalRows;
    const first = rows[0];
    const last = rows[rows.length - 1];
    if (!first || !last || first.total === null || last.total === null || rows.length < 2) {
      return this.rv5('trendTotalIncomplete');
    }
    const delta = last.total - first.total;
    const key = delta < 0 ? 'trendTotalLower' : delta > 0 ? 'trendTotalHigher' : 'trendTotalSame';
    return this.rv5(key, {
      points: Math.abs(delta),
      from: first.total,
      to: last.total,
      date: first.dateText,
    });
  }

  private get trendDates(): string[] {
    return (this.data?.domainTrends?.dates || []).filter(Boolean);
  }

  /** Each cognitive domain across the mappings, on the 0-100 scale THINK uses. */
  get cognitiveTrendRows(): DomainTrendRow[] {
    const dates = this.trendDates;
    const rows = this.data?.domainTrends?.cognitive || [];
    if (!dates.length || !rows.length) return [];
    return rows.map((row) => {
      const series = row.series.slice(0, dates.length);
      return {
        key: row.key,
        label: this.signal(row.key),
        meta: '',
        ...this.trendRange(series),
        valuesText: this.trendValuesText(series, dates),
        chart: this.buildMiniChart(series, dates, 100),
      };
    });
  }

  /** The mappings a domain actually measured something in. */
  private measuredDateIndexes(rows: Array<{ series: Array<number | null> }>): number[] {
    return this.trendDates
      .map((_, index) => index)
      .filter((index) => rows.some((row) => typeof row.series[index] === 'number'));
  }

  /** The cognitive card's own line: the scale, and how many mappings HAVE a
   *  value. Counting every mapping claimed 61 when one of them was measured. */
  get cognitiveTrendMeta(): string {
    const n = this.measuredDateIndexes(this.data?.domainTrends?.cognitive || []).length;
    return this.rv5(n === 1 ? 'trendCognitiveNoteOne' : 'trendCognitiveNote', { n });
  }

  /** Vision and hearing, read from her sense records the way the sense card does. */
  get senseTrendRows(): DomainTrendRow[] {
    const scans: any[] = this.data?.sense?.scans || [];
    if (!scans.length) return [];
    const metrics: Array<{
      metric: SenseMetric;
      titleKey: string;
      labelKey: string;
      digits: number;
      unit: string;
    }> = [
      { metric: 'vision', titleKey: 'trendVisionTitle', labelKey: 'doctorReport.sense.contrast', digits: 2, unit: 'logCS' },
      { metric: 'hearing', titleKey: 'trendHearingTitle', labelKey: 'doctorReport.sense.tone', digits: 1, unit: 'dB SNR' },
    ];
    const dates = scans.map((scan) => scan?.taken_at || '');
    return metrics.map(({ metric, titleKey, labelKey, digits, unit }) => {
      const series = scans.map((scan) => {
        const value = metricValue(scan, metric);
        return typeof value === 'number' ? Number(value.toFixed(digits)) : null;
      });
      const known = series.filter((v): v is number => typeof v === 'number');
      // The unit belongs to the number, not to a line of its own: what reads at a
      // glance is "1.20 logCS to 1.50 logCS", one quantity with its scale on it.
      const withUnit = (value: number) => value + ' ' + unit;
      const notMeasured = this.tx('doctorReport.reportView.v4.notMeasured', 'Not measured');
      return {
        key: metric,
        label: this.rv5(titleKey),
        meta: this.translation.translate(labelKey),
        // Nothing measured, or measured once: one reading, not a range.
        firstText: known.length ? withUnit(known[0]) : notMeasured,
        lastText: known.length > 1 ? withUnit(known[known.length - 1]) : '',
        valuesText: this.trendValuesText(series, dates, ' ' + unit),
        chart: this.buildMiniChart(series, dates),
      };
    });
  }

  /** What the selfie mapping recorded, mapping by mapping. */
  get facialTrendRows(): DomainTrendRow[] {
    const dates = this.trendDates;
    const rows = this.data?.domainTrends?.facial || [];
    if (!dates.length || !rows.length) return [];
    return rows.map((row) => {
      const series = row.series.slice(0, dates.length);
      return {
        key: row.key,
        label: this.signal(row.key),
        meta: '',
        ...this.trendRange(series),
        valuesText: this.trendValuesText(series, dates),
        chart: this.buildMiniChart(series, dates, 100),
        // The reference opens a visual-mapping row as one chip per mapping, the
        // date over what was recorded, so the row carries its own history.
        points: series
          .map((value, i) => ({ value, date: dates[i] }))
          .filter((point): point is { value: number; date: string } => typeof point.value === 'number')
          .slice(-ReportViewComponent.TREND_VALUES_SHOWN)
          .map((point) => ({
            dateText: this.formatShortDate(point.date),
            valueText: this.trendValueText(point.value),
          })),
      };
    });
  }

  /** The section is offered only when at least one of its parts has real data. */
  get hasAdditionalTrends(): boolean {
    return (
      this.symptomTotalRows.length > 0 ||
      this.cognitiveTrendRows.length > 0 ||
      this.senseTrendRows.length > 0 ||
      this.facialTrendRows.length > 0
    );
  }

  /** Every symptom with at least one rating across the mappings, with its latest value. */
  get availableSeries(): Array<{
    key: string;
    label: string;
    color: string;
    latest: number;
    selected: boolean;
  }> {
    const points = this.timelinePoints;
    if (!points.length) return [];
    const keys = new Set<string>();
    for (const point of points) {
      for (const key of Object.keys(point.symptoms || {})) keys.add(key);
    }
    const chosen = this.activeSeriesKeys;
    return [...keys]
      .map((key) => {
        const last = [...points]
          .reverse()
          .find((p) => p.symptoms?.[key] !== undefined);
        return {
          key,
          label: this.symptom(key),
          color: this.symptomColor(key),
          latest: Number(last?.symptoms?.[key] ?? 0),
          selected: chosen.includes(key),
        };
      })
      .sort((a, b) => b.latest - a.latest || a.label.localeCompare(b.label));
  }

  /** The keys actually drawn right now. */
  get activeSeriesKeys(): string[] {
    if (this.selectedSeriesKeys) return [...this.selectedSeriesKeys];
    return this.defaultSeriesKeys;
  }

  /** The set the report opens with: the tracked symptoms, then her prominent ones. */
  private get defaultSeriesKeys(): string[] {
    const points = this.timelinePoints;
    if (!points.length) return [];
    const has = (key: string) =>
      points.some((point) => point.symptoms?.[key] !== undefined);
    return [
      ...TIMELINE_KEYS.filter(has),
      ...(this.data?.topSymptoms || [])
        .map((symptom) => symptom.key)
        .filter((key) => !TIMELINE_KEYS.includes(key) && has(key)),
    ].slice(0, 8);
  }

  toggleSeries(key: string): void {
    const next = new Set(this.activeSeriesKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this.selectedSeriesKeys = next;
  }

  /** Add all / add everything she rated above 5 / clear, as in the design. */
  bulkSeries(mode: 'all' | 'high' | 'none'): void {
    if (mode === 'none') {
      this.selectedSeriesKeys = new Set();
      return;
    }
    const all = this.availableSeries;
    if (mode === 'all') {
      this.selectedSeriesKeys = new Set(all.map((s) => s.key));
      return;
    }
    const next = new Set(this.activeSeriesKeys);
    for (const series of all) if (series.latest > 5) next.add(series.key);
    this.selectedSeriesKeys = next;
  }

  /** The window the chart covers, as a sentence. */
  get timelineRangeText(): string {
    const points = this.timelinePoints;
    if (!points.length) return '';
    const from = this.reportDate(points[0].date);
    const to = this.reportDate(points[points.length - 1].date);
    const range =
      points.length > 1 ? this.rv5('timelineRange', { from, to }) : to;
    return `${range} · ${this.mappingCountText}`;
  }

  /**
   * The treatment periods she reported, as bands behind the lines: from the date
   * she says she started to the latest mapping. A medication with no start date
   * cannot be placed on a time axis and is left out of the drawing (it is still
   * listed in the chronology below the chart).
   */
  get treatmentBands(): Array<{
    label: string;
    x: number;
    width: number;
    color: string;
  }> {
    if (!this.showTreatmentLayer) return [];
    const points = this.timelinePoints;
    if (points.length < 2) return [];
    const right = this.xForDate(points[points.length - 1].date);
    const bands: Array<{ label: string; x: number; width: number; color: string }> = [];
    for (const med of this.medications) {
      if (!med.startDate) continue;
      const x = this.xForDate(med.startDate);
      if (!Number.isFinite(x)) continue;
      const clamped = Math.max(this.chartLeft, Math.min(x, right));
      const width = right - clamped;
      if (width <= 1) continue;
      bands.push({
        label: med.name || this.rv('unnamedMedication'),
        x: clamped,
        width,
        color: this.isHrt(med) ? 'rgba(207,166,204,.065)' : 'rgba(101,105,117,.05)',
      });
    }
    return bands.slice(0, 3);
  }

  /** What she reported starting and when, oldest first. */
  get medicationChronology(): Array<{ date: string; label: string; detail: string }> {
    return this.medications
      .filter((med) => !!med.startDate)
      .sort((a, b) => (a.startDate < b.startDate ? -1 : 1))
      .map((med) => ({
        date: this.reportDate(med.startDate),
        label: med.name || this.rv('unnamedMedication'),
        detail: [
          med.dose,
          this.formatFrequency(med.frequency, med.frequencyCustom),
          med.reason,
        ]
          .filter((part) => !!(part || '').trim())
          .join(' · '),
      }));
  }

  // ---------------------------------------------------------------------------
  // EXPLORER PANEL. Her symptom groups, searchable, next to the four measured
  // perspectives she completed.
  // ---------------------------------------------------------------------------

  symptomSearch = '';

  /**
   * Her symptom groups in one shape, whether or not she has history: name, how
   * many are shown, and each rated symptom inside it. Filtered by the search box
   * on either the group name or a symptom name.
   */
  get explorerGroups(): Array<{
    label: string;
    color: string;
    count: number;
    lines: Array<{ label: string; score: number; change?: string }>;
  }> {
    const groups: Array<{
      label: string;
      color: string;
      count: number;
      lines: Array<{ label: string; score: number; change?: string }>;
    }> = this.hasTrends
      ? this.clusterSummaries.map((cluster) => ({
          label: cluster.label,
          color: cluster.color,
          count: cluster.count,
          lines: cluster.lines.map((line) => ({
            label: line.label,
            score: line.score,
            change: line.change,
          })),
        }))
      : this.compositionClusters.map((cluster) => ({
          label: cluster.label,
          color: cluster.color,
          count: cluster.count,
          lines: cluster.lines.map((line) => ({
            label: line.label,
            score: line.score,
          })),
        }));

    // The Explorer reads her RATINGS, so a row she rated 0 is not a rated symptom.
    // The follow-up cluster cards deliberately keep a dropped-to-zero row (that
    // improvement is the Timeline story), but carrying it here made a card claim
    // "1 rated symptom" over a 0.0/10 average with "Highest: ... 0/10". Count,
    // average and highest all read rows above 0, and a group with nothing above 0
    // is not shown at all.
    const rated = groups
      .map((group) => {
        const lines = group.lines.filter((line) => Number(line.score) > 0);
        return lines.length ? { ...group, count: lines.length, lines } : null;
      })
      .filter((group): group is NonNullable<typeof group> => group !== null);

    const term = this.symptomSearch.trim().toLowerCase();
    if (!term) return rated;
    return rated
      .map((group) => {
        if (group.label.toLowerCase().includes(term)) return group;
        const lines = group.lines.filter((line) =>
          line.label.toLowerCase().includes(term)
        );
        return lines.length ? { ...group, lines } : null;
      })
      .filter((group): group is NonNullable<typeof group> => group !== null);
  }


  /**
   * The same groups, read the way the record reads them: how many she rated, the
   * average of those ratings, and the one she rated highest. The list itself is
   * kept behind a disclosure, so the card is a summary and not a wall.
   */
  get explorerGroupCards(): Array<{
    label: string;
    color: string;
    count: number;
    avgText: string;
    avgPercent: number;
    topLabel: string;
    topScore: number | null;
    lines: Array<{ label: string; score: number; change?: string }>;
  }> {
    return this.explorerGroups.map((group) => {
      const scores = group.lines
        .map((line) => Number(line.score))
        .filter((value) => Number.isFinite(value));
      const avg = scores.length
        ? scores.reduce((sum, value) => sum + value, 0) / scores.length
        : null;
      const top = group.lines
        .slice()
        .sort((a, b) => Number(b.score) - Number(a.score))[0];
      return {
        ...group,
        avgText: avg === null ? '-' : avg.toFixed(1),
        avgPercent: avg === null ? 0 : Math.max(0, Math.min(100, avg * 10)),
        topLabel: top ? top.label : '',
        topScore: top ? Number(top.score) : null,
      };
    });
  }

  /** Every cognitive value she has, one column per mapping. The bars answer
   *  "where is she now"; the clinician also wants the numbers themselves, the
   *  way the reference record prints them (Danna, 17.09), so the two sit
   *  together: the bars first, this table under them. */
  get cognitiveValueTable(): { dates: string[]; rows: Array<{ label: string; values: string[] }> } | null {
    const dates = this.trendDates;
    const rows = this.data?.domainTrends?.cognitive || [];
    if (!dates.length || !rows.length) return null;
    // Only the mappings that measured something get a column, and at most the
    // last dozen of those: an empty column per mapping is a wall of "not
    // measured" on an account with sixty of them (Danna, 17.09).
    const columns = this.measuredDateIndexes(rows).slice(-ReportViewComponent.TREND_VALUES_SHOWN);
    if (!columns.length) return null;
    return {
      dates: columns.map((index) => this.formatShortDate(dates[index])),
      rows: rows.map((row) => ({
        label: this.signal(row.key),
        // A cell with nothing in it is a dash, not the words "not measured"
        // repeated across the row: the table is read as numbers.
        values: columns.map((index) =>
          typeof row.series[index] === 'number' ? String(row.series[index]) : '-',
        ),
      })),
    };
  }

  /** How many tasks the cognitive battery produced a value for. */
  get cognitiveTaskCount(): number {
    return this.cognitiveRows.filter((row) => !row.notMeasured).length;
  }

  /** The date of the mapping this report is written from, short form. */
  get latestMappingDate(): string {
    const dates = this.trendDates;
    const last = dates.length ? dates[dates.length - 1] : this.data?.checkDate;
    return last ? this.formatShortDate(last) : '';
  }

  /** The vision / hearing entry line: the latest reading, or that it was not measured. */
  senseLatest(metric: SenseMetric): string {
    const rows = this.senseTrendRows.filter((row) => row.key === metric);
    const row = rows.length ? rows[0] : null;
    if (!row) return this.tx('doctorReport.reportView.v4.notMeasured', 'Not measured');
    return row.lastText || row.firstText;
  }

  /** The rows of one sense metric, for the body of its entry card. */
  senseRowsFor(metric: SenseMetric): SenseReportRow[] {
    const block = this.senseBlock;
    if (!block) return [];
    return block.rows.filter((row) => row.metric === metric);
  }

  /**
   * What the mapping read from her selfie, as label / value / change rows. The
   * fourth perspective in the explorer, and context rather than a finding.
   */
  get physiologicalSignalRows(): Array<{ label: string; value: string; delta: string }> {
    return (this.data?.physiologicalSignals || []).map((entry) => ({
      label: this.signal(entry.key),
      value: entry.value,
      delta: entry.delta || '',
    }));
  }


  // ---------------------------------------------------------------------------
  // PAIRINGS. A symptom she reported, placed beside the measurement of ours that
  // looks at the same ground. Two scales side by side and nothing more: a pair
  // never confirms a symptom, never explains it, and never says one caused the
  // other. Only pairs where BOTH sides have a value on this mapping are shown.
  // ---------------------------------------------------------------------------

  /** The fixed table. Hand-picked, deliberately conservative, never inferred. */
  private readonly SYMPTOM_MEASURE_PAIRS: Array<{
    symptom: string;
    source: 'cognitive' | 'sense' | 'facial';
    measure: string;
  }> = [
    { symptom: 'brain_fog', source: 'cognitive', measure: 'wordRetrieval' },
    { symptom: 'slow_recall', source: 'cognitive', measure: 'delayedRecall' },
    { symptom: 'forgetfulness', source: 'cognitive', measure: 'workingMemorySpan' },
    {
      symptom: 'difficulty_concentrating',
      source: 'cognitive',
      measure: 'reactionTime',
    },
    { symptom: 'unrefreshing_sleep', source: 'cognitive', measure: 'reactionTime' },
    { symptom: 'vision_fluctuation', source: 'sense', measure: 'vision' },
    { symptom: 'dry_eyes_gritty_feeling', source: 'sense', measure: 'vision' },
    { symptom: 'tinnitus_ringing', source: 'sense', measure: 'hearing' },
    { symptom: 'dry_skin', source: 'facial', measure: 'skinDryness' },
  ];

  /**
   * The pairs that can actually be drawn for this mapping: she rated the symptom
   * above 0 and the matching measurement produced a value. A measurement is used
   * at most once, so the panel does not repeat the same task three times.
   */
  get symptomMeasurePairs(): Array<{
    symptomLabel: string;
    symptomNow: number;
    symptomPrev: number | null;
    measureLabel: string;
    measureValue: string;
    measureChange: string;
    source: 'cognitive' | 'sense' | 'facial';
    measureFirst: string;
    measureLast: string;
    measureUnit: string;
    rangeText: string;
    reading: string;
  }> {
    const current = this.currentSymptoms;
    const points = this.timelinePoints;
    const previous =
      points.length > 1 ? points[points.length - 2]?.symptoms || {} : {};
    const usedMeasures = new Set<string>();
    const out: Array<{
      symptomLabel: string;
      symptomNow: number;
      symptomPrev: number | null;
      measureLabel: string;
      measureValue: string;
      measureChange: string;
      source: 'cognitive' | 'sense' | 'facial';
      measureFirst: string;
      measureLast: string;
      measureUnit: string;
      rangeText: string;
      reading: string;
    }> = [];

    for (const pair of this.SYMPTOM_MEASURE_PAIRS) {
      const now = Number(current[pair.symptom] || 0);
      if (!now) continue;
      const measured = this.measurementFor(pair.source, pair.measure);
      if (!measured) continue;
      const measureId = `${pair.source}:${pair.measure}`;
      if (usedMeasures.has(measureId)) continue;
      usedMeasures.add(measureId);
      const prevRaw = previous[pair.symptom];
      const symptomPrev = typeof prevRaw === 'number' ? prevRaw : null;
      const symptomLabel = this.symptom(pair.symptom);
      // The measurement read across the mappings, so the pair shows two moves
      // rather than two loose numbers. Where there is no history it shows one.
      const span = this.measureSpan(pair.source, pair.measure);
      out.push({
        symptomLabel,
        symptomNow: now,
        symptomPrev,
        measureLabel: measured.label,
        measureValue: measured.value,
        measureChange: measured.change,
        source: pair.source,
        measureFirst: span.firstText,
        measureLast: span.lastText || measured.value,
        measureUnit: span.unit,
        rangeText: span.rangeText,
        reading: this.pairReading(
          symptomLabel,
          symptomPrev,
          now,
          measured.label,
          span,
          measured.value,
        ),
      });
    }
    return out;
  }

  /**
   * The measurement across her mappings: where it started, where it stands, and
   * the dates between. A module with no history returns only the latest value.
   */
  private measureSpan(
    source: 'cognitive' | 'sense' | 'facial',
    measure: string,
  ): { firstText: string; lastText: string; unit: string; rangeText: string; direction: 'up' | 'down' | 'same' | null } {
    const empty = { firstText: '', lastText: '', unit: '', rangeText: '', direction: null as null };
    if (source === 'sense') {
      const row = this.senseTrendRows.find((r) => r.key === measure);
      if (!row) return empty;
      const dates = (this.data?.sense?.scans || []).map((scan: any) => scan?.taken_at || '');
      return {
        firstText: row.lastText ? row.firstText : '',
        lastText: row.lastText || row.firstText,
        unit: '',
        rangeText: this.spanDates(dates),
        direction: this.moveOf(row.firstText, row.lastText),
      };
    }
    const rows =
      source === 'cognitive' ? this.cognitiveTrendRows : this.facialTrendRows;
    const row = rows.find((r) => r.key === measure);
    if (!row) return empty;
    return {
      firstText: row.lastText ? row.firstText : '',
      lastText: row.lastText || row.firstText,
      unit: '/100',
      rangeText: this.spanDates(this.trendDates),
      direction: this.moveOf(row.firstText, row.lastText),
    };
  }

  /** "12.06.26 to 21.06.26", or just the one date when that is all there is. */
  private spanDates(dates: string[]): string {
    const known = (dates || []).filter(Boolean);
    if (!known.length) return '';
    const first = this.formatShortDate(known[0]);
    const last = this.formatShortDate(known[known.length - 1]);
    return first === last ? last : this.rv5('pairRange', { from: first, to: last });
  }

  private moveOf(first: string, last: string): 'up' | 'down' | 'same' | null {
    const a = parseFloat(first);
    const b = parseFloat(last);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return b > a ? 'up' : b < a ? 'down' : 'same';
  }

  /**
   * The pair said in one plain sentence: which way her rating went, and which way
   * the measurement went. Nothing is claimed about one explaining the other.
   */
  private pairReading(
    symptomLabel: string,
    symptomPrev: number | null,
    symptomNow: number,
    measureLabel: string,
    span: { direction: 'up' | 'down' | 'same' | null; lastText: string },
    measureValue: string,
  ): string {
    if (symptomPrev === null || !span.lastText) {
      return this.rv5('pairFirstReading');
    }
    const symptomMove = this.rv5(
      symptomNow < symptomPrev
        ? 'pairSymLower'
        : symptomNow > symptomPrev
          ? 'pairSymHigher'
          : 'pairSymSame',
    );
    const measureMove =
      span.direction === 'up'
        ? this.rv5('pairMeasUp')
        : span.direction === 'down'
          ? this.rv5('pairMeasDown')
          : span.direction === 'same'
            ? this.rv5('pairMeasSame')
            : this.rv5('pairMeasRecorded', { value: measureValue });
    return this.rv5('pairReading', {
      symptom: symptomLabel,
      symptomMove,
      measure: measureLabel,
      measureMove,
    });
  }

  /** One measurement, whichever module it came from, as label / value / change. */
  private measurementFor(
    source: 'cognitive' | 'sense' | 'facial',
    measure: string
  ): { label: string; value: string; change: string } | null {
    if (source === 'cognitive') {
      const row = this.cognitiveRows.find(
        (r) => r.key === measure && !r.notMeasured
      );
      if (!row) return null;
      return {
        label: row.label,
        value: row.bandLabel,
        // The movement shown on the card, when there is history to move against.
        change: row.trend || '',
      };
    }
    if (source === 'sense') {
      const block = this.senseBlock;
      if (!block) return null;
      const row = block.rows.find((r) => r.metric === measure);
      if (!row || !row.currentValue) return null;
      return {
        label: this.translation.translate(row.labelKey),
        value: row.currentValue,
        change: row.changeText || '',
      };
    }
    const signal = (this.data?.physiologicalSignals || []).find(
      (s) => s.key === measure
    );
    if (!signal || !signal.value) return null;
    return {
      label: this.signal(signal.key),
      value: signal.value,
      change: signal.delta || '',
    };
  }


  // ---------------------------------------------------------------------------
  // COMPARISON PERIOD. The digest normally compares this mapping with the one
  // before it. When she reported starting something, the clinician can switch to
  // "since this treatment period began" instead, which compares against the last
  // mapping taken BEFORE that start date. Which medication defines the period:
  // the earliest start date she reported, matching the design's single treatment
  // track. Nothing is attributed to the medication; only the window moves.
  // ---------------------------------------------------------------------------

  comparisonMode: 'previous' | 'treatment' = 'previous';

  setComparisonMode(mode: 'previous' | 'treatment'): void {
    this.comparisonMode = mode;
  }

  /** The earliest start date she reported for anything she is taking. */
  get treatmentStartDate(): string | null {
    const dated = this.medications
      .map((med) => (med.startDate || '').trim())
      .filter((date) => !!date)
      .sort();
    return dated.length ? dated[0] : null;
  }

  /** The last mapping taken at or before that start: the treatment baseline. */
  private get treatmentBasePoint(): ReportTimelinePoint | null {
    const start = this.treatmentStartDate;
    if (!start) return null;
    const points = this.ratedTimelinePoints;
    if (points.length < 2) return null;
    const startTime = Date.parse(start);
    if (Number.isNaN(startTime)) return null;
    let base: ReportTimelinePoint | null = null;
    for (const point of points.slice(0, -1)) {
      const time = Date.parse(point.date);
      if (!Number.isNaN(time) && time <= startTime) base = point;
    }
    return base;
  }

  /**
   * The switch is offered only when it would actually say something different:
   * a treatment baseline exists and it is not simply the previous mapping.
   */
  get canCompareTreatment(): boolean {
    const base = this.treatmentBasePoint;
    if (!base) return false;
    const previous = this.previousRatedPoint;
    return !!previous && base.date !== previous.date;
  }

  /** The mapping the digest is comparing against right now. */
  private get comparisonBasePoint(): ReportTimelinePoint | null {
    if (this.comparisonMode === 'treatment' && this.canCompareTreatment) {
      return this.treatmentBasePoint;
    }
    return this.previousRatedPoint;
  }

  /** Which of the two headings the digest carries. */
  get digestTitle(): string {
    if (this.isSingleCheck) return this.rv5('digestTitleFirst');
    return this.comparisonMode === 'treatment' && this.canCompareTreatment
      ? this.rv5('digestTitleTreatment')
      : this.rv5('digestTitlePrevious');
  }

  /** The two dates the comparison spans, said once under the counts. */
  get comparisonRangeText(): string {
    const base = this.comparisonBasePoint;
    const points = this.ratedTimelinePoints;
    if (!base || !points.length) return '';
    return this.rv5('baselineLine', {
      from: this.reportDate(base.date),
      to: this.reportDate(points[points.length - 1].date),
    });
  }

  /**
   * Every rated symptom that moved against the chosen baseline. Used only when
   * the treatment window is selected; the default previous-mapping lists stay on
   * the existing (validated) getters so the report reads the same as before.
   */
  private directionRowsAgainst(
    base: ReportTimelinePoint
  ): Array<{ label: string; prev: number; now: number; delta: number }> {
    const current = this.currentSymptoms;
    const rows: Array<{ label: string; prev: number; now: number; delta: number }> = [];
    for (const [key, raw] of Object.entries(current)) {
      const prevRaw = base.symptoms?.[key];
      if (prevRaw === undefined || prevRaw === null) continue;
      const prev = Number(prevRaw);
      const now = Number(raw || 0);
      if (now === prev) continue;
      rows.push({ label: this.symptom(key), prev, now, delta: now - prev });
    }
    return rows;
  }

  /** The bleeding pattern list, as she marked it. */
  private get bleedingPatternText(): string {
    const patterns = this.data?.bleedingPatterns || [];
    if (!patterns.length) return '';
    return this.formatBleedingPatterns(patterns);
  }

  /** The bleeding yes/no flags. Only the ones she answered are named. */
  private get bleedingFlagText(): string {
    const detail = this.data?.bleedingDetail;
    if (!detail) return '';
    const keys: Array<keyof typeof detail> = [
      'heavy',
      'spotting',
      'newOrUnexplained',
      'postAny',
      'postHeavy',
    ];
    const parts: string[] = [];
    for (const key of keys) {
      const value = detail[key];
      if (value === null || value === undefined) continue;
      const label = this.tx(
        `doctorReport.bleeding.detail.${key}.q`,
        String(key)
      );
      parts.push(`${label} ${value ? this.rv5('yes') : this.rv5('no')}`);
    }
    return parts.join(' · ');
  }

  /**
   * The gynaecological procedure block from the first questionnaire, read with
   * the questionnaire's own option labels. The answers are stored snake_case and
   * the labels are keyed camelCase, so the key is converted rather than guessed.
   */
  private get surgeryText(): string {
    const s = this.data?.intake?.surgery;
    if (!s || !s.procedure) return '';
    const camel = (value: string) =>
      value.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
    const parts: string[] = [
      this.tx(
        `symptoms.surgery.procedureOptions.${camel(s.procedure)}`,
        s.procedure.replace(/_/g, ' ')
      ),
    ];
    if (s.ovaries_removed) {
      parts.push(
        this.rv5('surgeryOvaries', {
          count: this.tx(
            `symptoms.surgery.ovaryCount.${camel(s.ovaries_removed)}`,
            s.ovaries_removed.replace(/_/g, ' ')
          ),
        })
      );
    }
    if (s.year) parts.push(String(s.year));
    if (s.hormone_therapy) {
      const key =
        s.hormone_therapy === 'yes'
          ? 'surgeryHormoneYes'
          : s.hormone_therapy === 'no'
            ? 'surgeryHormoneNo'
            : 'surgeryHormoneNotSure';
      parts.push(
        this.rv5(key) +
          (s.hormone_therapy === 'yes' && s.hormone_therapy_year
            ? ` ${s.hormone_therapy_year}`
            : '')
      );
    }
    return parts.join(' · ');
  }

  /**
   * Allergies as we hold them: the rating she gave "new or stronger allergies"
   * plus the follow-up on whether they predate this period. This is not a
   * drug-allergy list, so the row never claims anything about those.
   */
  private get allergyText(): string {
    const value = Number(this.currentSymptoms['allergy_increase'] || 0);
    if (!value) return '';
    const prior = this.data?.intake?.symptomFollowUps?.['allergy_increase'];
    const parts = [`${this.symptom('allergy_increase')} ${value}/10`];
    if (prior === 'yes') parts.push(this.rv5('allergyBefore'));
    else if (prior === 'no') parts.push(this.rv5('allergyNew'));
    return parts.join(' · ');
  }

  /**
   * Her lifestyle answers as localised rows. Built from the raw answers with the
   * questionnaire's own option labels, so each row reads in the report language.
   * An unanswered question produces no row. Nothing is scored or judged.
   */
  private get lifestyleRows(): Array<{ label: string; value: string }> {
    const life = this.data?.lifestyle;
    if (!life) return [];
    const rows: Array<{ label: string; value: string }> = [];
    const opt = (path: string) => this.tx(`doctorReport.lifestyle.${path}`, '');
    const add = (label: string, value: string) => {
      if (value) rows.push({ label, value });
    };

    if (life.alcohol) add(this.rv5('histAlcohol'), opt(`alcohol.${life.alcohol}`));
    if (life.caffeine)
      add(this.rv5('histCaffeine'), opt(`caffeine.${life.caffeine}`));

    const exercise: string[] = [];
    if (life.exerciseWeekly)
      exercise.push(opt(`exercise.${life.exerciseWeekly}`));
    const types = (life.exerciseTypes || [])
      .map((t) => opt(`exercise.type_${t}`))
      .filter(Boolean);
    if (life.exerciseOther?.trim()) types.push(life.exerciseOther.trim());
    if (types.length) exercise.push(types.join(', '));
    if (life.exerciseChange)
      exercise.push(opt(`exercise.change_${life.exerciseChange}`));
    if (life.exerciseNote?.trim()) exercise.push(life.exerciseNote.trim());
    add(this.rv5('histExercise'), exercise.filter(Boolean).join(' · '));

    const sleep: string[] = [];
    if (life.sleepHours) sleep.push(opt(`sleep.hours_${life.sleepHours}`));
    if (life.sleepWaking) sleep.push(opt(`sleep.waking_${life.sleepWaking}`));
    if (life.sleepChange) sleep.push(opt(`sleep.change_${life.sleepChange}`));
    if (life.sleepNote?.trim()) sleep.push(life.sleepNote.trim());
    add(this.rv5('histSleep'), sleep.filter(Boolean).join(' · '));

    if (life.stress) add(this.rv5('histStress'), opt(`stress.${life.stress}`));

    if (life.smoking) {
      const smoking = [opt(`smoking.${life.smoking}`)];
      if (life.smoking === 'former' && life.smokingQuitYear?.trim()) {
        smoking.push(life.smokingQuitYear.trim());
      }
      add(this.rv5('histSmoking'), smoking.filter(Boolean).join(' · '));
    }

    const meals: string[] = [];
    if (life.mealsHome) meals.push(opt(`meals.home_${life.mealsHome}`));
    if (life.hotMeals) meals.push(opt(`meals.hot_${life.hotMeals}`));
    add(this.rv5('histMeals'), meals.filter(Boolean).join(' · '));

    return rows;
  }

  private changeText(previous: number, current: number): string {
    const diff = current - previous;
    if (diff <= -1) return this.rv('changeDecrease', { n: Math.abs(diff) });
    if (diff >= 1) return this.rv('changeIncrease', { n: diff });
    return this.rv('changeStable');
  }

  /**
   * Colours are ASSIGNED across the symptoms this report can draw, not hashed from
   * the key. A hash cannot promise distinctness and this one did not deliver it:
   * it shipped a default chart where night sweats and trouble falling asleep were
   * the same ochre and energy dips and urinary frequency the same teal, in the
   * lines, the legend and the picker dots alike. The eight named symptoms are
   * seeded first so they keep their colour between mappings, then everyone else
   * takes the next entry still free, walking a stable (alphabetical) order so the
   * assignment does not shift when she toggles a series. Past twenty-four rated
   * symptoms it wraps, which no fixture or real mapping reaches.
   */
  private seriesColorCache: { signature: string; map: Record<string, string> } | null = null;

  private get seriesColorMap(): Record<string, string> {
    const keys = new Set<string>();
    for (const point of this.timelinePoints) {
      for (const key of Object.keys(point.symptoms || {})) keys.add(key);
    }
    const ordered = [...keys].sort();
    // symptomColor is called once per series per render, so the assignment is
    // memoised on the key set rather than rebuilt inside every loop.
    const signature = ordered.join('|');
    if (this.seriesColorCache?.signature === signature) return this.seriesColorCache.map;
    const map: Record<string, string> = {};
    const taken = new Set<string>();
    for (const key of ordered) {
      const named = SYMPTOM_COLORS[key];
      if (named && !taken.has(named)) {
        map[key] = named;
        taken.add(named);
      }
    }
    let next = 0;
    for (const key of ordered) {
      if (map[key]) continue;
      while (taken.has(SERIES_PALETTE[next]) && taken.size < SERIES_PALETTE.length) {
        next = (next + 1) % SERIES_PALETTE.length;
      }
      const colour = SERIES_PALETTE[next];
      map[key] = colour;
      taken.add(colour);
      next = (next + 1) % SERIES_PALETTE.length;
    }
    this.seriesColorCache = { signature, map };
    return map;
  }

  private symptomColor(key: string): string {
    return this.seriesColorMap[key] || SYMPTOM_COLORS[key] || SERIES_PALETTE[0];
  }


  private cognitiveScore(signal: ReportSignalEntry): number {
    // Single source of truth: the server now sends the normalized 0-100 score
    // computed with the same formula as the consumer THINK screen. Use it
    // directly so the report can never disagree with the user's own results.
    // Fallback below is only for older reports generated before `score` existed.
    if (typeof signal.score === 'number' && Number.isFinite(signal.score)) {
      return Math.max(0, Math.min(100, Math.round(signal.score)));
    }
    const raw = String(signal.value || '');
    if (signal.key === 'workingMemorySpan') {
      const parts = raw.match(/\d+(?:\.\d+)?/g)?.map(Number) || [];
      if (parts.length) return Math.max(0, Math.min(100, Math.round((parts.reduce((sum, value) => sum + value, 0) / 10) * 100)));
    }
    if (signal.key === 'delayedRecall') {
      const first = Number(raw.match(/\d+(?:\.\d+)?/)?.[0]);
      if (Number.isFinite(first)) return Math.max(0, Math.min(100, Math.round((first / 4) * 100)));
    }
    if (signal.key === 'wordRetrieval') {
      const numeric = Number(raw.replace(/[^\d.-]/g, ''));
      if (Number.isFinite(numeric) && numeric <= 30) return Math.max(0, Math.min(100, Math.round((numeric / 20) * 100)));
    }
    return this.signalScore(signal);
  }

  private signalTrend(signal: ReportSignalEntry): string {
    if (!signal.delta || signal.delta === 'stable' || signal.delta === '0') return this.rv('trendStable');
    return signal.isPositive ? this.rv('trendUp') : this.rv('trendDown');
  }

  private firstSymptomWeek(key: string): number | null {
    const point = this.timelinePoints.find((item) => Number(item.symptoms?.[key] || 0) > 0);
    return point ? point.week : null;
  }

  /** First reported (>0) value for a symptom across the timeline (baseline), and
   *  the date it was first reported  - used by the cross-reference "resolved / since"
   *  wording (fix 23) so it reads a real DATE, never an internal "week N" index. */
  private firstSymptomValue(key: string): number | null {
    const point = this.timelinePoints.find((item) => Number(item.symptoms?.[key] || 0) > 0);
    return point ? Number(point.symptoms[key]) : null;
  }

  private firstSymptomDate(key: string): string | null {
    const point = this.timelinePoints.find((item) => Number(item.symptoms?.[key] || 0) > 0);
    return point && point.date ? this.reportDate(point.date) : null;
  }

  /** Fix 25: ONE comparison baseline for the whole report  - the PREVIOUS RATED scan
   *  (the same one the server uses for the top-symptom deltas), never "first in the
   *  window". So the summary, the attention card, the graph legend, the resolved
   *  list, the clusters and the cross-reference all quote the same delta for a
   *  symptom (no "+10 here / +5 there"). */
  private get ratedTimelinePoints(): ReportTimelinePoint[] {
    return this.timelinePoints.filter(
      (p) => p.symptoms && Object.keys(p.symptoms).length > 0,
    );
  }

  private get previousRatedPoint(): ReportTimelinePoint | null {
    const pts = this.ratedTimelinePoints;
    return pts.length >= 2 ? pts[pts.length - 2] : null;
  }

  /** Value of a symptom at the previous rated scan (baseline), or null when there
   *  is no previous rating for it  - matching the server delta (no baseline → no
   *  delta), so the whole report agrees. */
  private previousRatedValue(key: string): number | null {
    const p = this.previousRatedPoint;
    if (!p) return null;
    const v = p.symptoms[key];
    return v === undefined || v === null ? null : Number(v);
  }

  // The doctor-portal report lives at /app/doctor/report/:scanId (any clinic);
  // the woman's own report is /app/doctor-report/:scanId/view.
  private isMaccabiDoctorPortalRoute(): boolean {
    return /\/(?:app\/doctor\/(?:report|maccabi)|maccabi)(?:\/|$)/.test(this.router.url.toLowerCase());
  }

  private maccabiDoctorPortalRoute(...segments: string[]): string[] {
    const match = this.router.url.match(/^\/(en|he)(?:\/|$)/);
    return [`/${match?.[1] || 'he'}`, 'app', 'doctor', ...segments];
  }

  /**
   * One parser for every date the report reads. The order is the point: new Date()
   * takes "5.4.2026" for 4 May, so the day-first form the server sends has to be
   * matched BEFORE the built-in parser is allowed near it (it only failed loudly on
   * days past the 12th, which is why this read correctly most of the month). A
   * date-only string is anchored at local midnight so a clinician west of UTC is not
   * shown the previous day; a full timestamp keeps its own instant.
   */
  private parseReportDate(value?: string): number | null {
    if (!value) return null;
    const dayFirst = value.match(/^(\d{1,2})[-./](\d{1,2})[-./](\d{4})$/);
    if (dayFirst) {
      return new Date(Number(dayFirst[3]), Number(dayFirst[2]) - 1, Number(dayFirst[1])).getTime();
    }
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])).getTime();
    }
    const direct = new Date(value).getTime();
    return Number.isFinite(direct) ? direct : null;
  }

  private fallbackCopy(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.copyState = this.rv('copied');
    setTimeout(() => (this.copyState = ''), 1600);
  }
}
